import React, { useState, useRef, useEffect } from 'react';
import { withTranslation } from 'react-i18next';
import Select from 'react-select';
import flatpickr from 'flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import { Stepper, Step, StepLabel, StepConnector, withStyles, Button, TextField } from '@material-ui/core';
import { useSite } from '../../contexts/SiteContext';
import { trackFieldChange, trackStepView, trackNextClick, trackSubmitClick, trackFormComplete } from '../../hooks/useFormTracking';

// Custom Stepper Connector with green line for completed steps
const ColorlibConnector = withStyles({
  alternativeLabel: {
    top: 22,
  },
  active: {
    '& $line': {
      backgroundColor: '#28a745',
    },
  },
  completed: {
    '& $line': {
      backgroundColor: '#28a745',
    },
  },
  line: {
    height: 3,
    border: 0,
    backgroundColor: '#e0e0e0',
    borderRadius: 1,
  },
})(StepConnector);

// Custom Step Icon styles for green checkmark
const ColorlibStepIcon = withStyles({
  root: {
    backgroundColor: '#ccc',
    zIndex: 1,
    color: '#fff',
    width: 50,
    height: 50,
    display: 'flex',
    borderRadius: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: 18,
    fontWeight: 'bold',
  },
  active: {
    backgroundColor: '#110F5E',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  },
  completed: {
    backgroundColor: '#28a745',
  },
})(({ classes, active, completed, icon }) => (
  <div className={`${classes.root} ${active ? classes.active : ''} ${completed ? classes.completed : ''}`}>
    {completed ? '✓' : icon}
  </div>
));

/**
 * Check if the current site is a live production site
 * Returns true for live sites where dev tools should be hidden
 * During SSR, defaults to true to prevent DEV buttons from flashing
 */
const isLiveProduction = () => {
  // During SSR, default to true (hide dev buttons) to prevent flash
  if (typeof window === 'undefined') return true;
  
  const liveUrls = [
    'bmc.edu.sa',
    'www.bmc.edu.sa',
    'bmc.ac.ae',
    'www.bmc.ac.ae'
  ];
  
  return liveUrls.some(url => window.location.hostname === url);
};

/**
 * Sanitize input by removing invisible Unicode characters
 * Removes: RTL/LTR marks, zero-width spaces, BOM, bidirectional formatting, non-breaking spaces
 */
const sanitizeInvisibleChars = (str) => {
  if (!str) return str;
  // Remove invisible Unicode characters:
  // U+200B-U+200F - Zero-width spaces and RTL/LTR marks
  // U+202A-U+202E - Bidirectional formatting characters
  // U+FEFF - Byte Order Mark (BOM)
  // U+00A0 - Non-breaking space
  return str.replace(/[\u200B-\u200F\u202A-\u202E\uFEFF\u00A0]/g, '').trim();
};

/**
 * Sanitize phone number - keeps only digits and + sign
 */
const sanitizePhoneNumber = (phone) => {
  if (!phone) return phone;
  return phone.replace(/[^\d+]/g, '').trim();
};

// Campus code to label mapping (defined outside component to avoid recreating on each render)
const CAMPUS_MAPPING = {
  JED_OU: { en: 'Jeddah', ar: 'جدة' },
  ASR_OU: { en: 'Aseer', ar: 'عسير' },
  DAM_OU: { en: 'Dammam', ar: 'الدمام' },
  DXB_OU: { en: 'Dubai', ar: 'دبي' },
  MED_OU: { en: 'Madinah', ar: 'المدينة المنورة' }
};

/**
 * Application Wizard component with 4-step navigation (Next-only flow).
 */
const ApplicationWizard = (props) => {
  // Extract props - fields comes from Sitecore, t and i18n from withTranslation
  const { fields, t, i18n } = props;
  
  // Get current site context (KSA or UAE)
  const site = useSite();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [level, setLevel] = useState(null);
  const [campus, setCampus] = useState(null);
  const [campusOptions, setCampusOptions] = useState([]);
  const [allIntakesBuffer, setAllIntakesBuffer] = useState([]); // Buffer to store all intakes from API
  const [intake, setIntake] = useState(null);
  const [intakeOptions, setIntakeOptions] = useState([]);
  const [intakeLoading, setIntakeLoading] = useState(false);

  // Citizen value is site-specific: saudi_citizen for KSA, emirati_citizen for UAE
  const [residencyStatus, setResidencyStatus] = useState(site.isUAE ? 'emirati_citizen' : 'saudi_citizen');
  const [nationality, setNationality] = useState(null);
  const [nationalId, setNationalId] = useState('');
  const [nationalIdValidating, setNationalIdValidating] = useState(false);
  const [nationalIdValid, setNationalIdValid] = useState(false);
  const nationalIdInputRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [idExpiry, setIdExpiry] = useState(''); // Used for form submission
  const [gender, setGender] = useState('');
  const [dob, setDob] = useState('');
  const [firstNameEn, setFirstNameEn] = useState('');
  const [secondNameEn, setSecondNameEn] = useState('');
  const [thirdNameEn, setThirdNameEn] = useState('');
  const [lastNameEn, setLastNameEn] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [phone, setPhone] = useState('');
  const [emailError, setEmailError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState({ en: '', ar: '' });
  const [modalType, setModalType] = useState('warning'); // 'warning', 'success', 'info'
  
  // OTP Verification State
  const [EMAIL_VER, setEMAIL_VER] = useState(false);
  const [MOBILE_VER, setMOBILE_VER] = useState(false);
  const [skipMobileOtp, setSkipMobileOtp] = useState(false); // If true, hide Verify button for mobile
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpType, setOtpType] = useState(''); // 'email' or 'mobile'
  const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
  const [otpTimer, setOtpTimer] = useState(0);
  const [emailOtpSending, setEmailOtpSending] = useState(false);
  const [mobileOtpSending, setMobileOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const otpInputRefs = useRef([]);
  
  // Submission State
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [applicationId, setApplicationId] = useState(null);
  
  // Step 2 Accordion State - all items expanded by default
  const [expandedAccordions, setExpandedAccordions] = useState({});
  
  // Rules Acceptance State (Step 1)
  const [rulesAccepted, setRulesAccepted] = useState(false);
  const [showRulesPopup, setShowRulesPopup] = useState(false);
  
  // Step 3 Educational Information State
  const [applicationType, setApplicationType] = useState(null);
  const [examResult, setExamResult] = useState(null); // true/false
  const [uaeWaitingExamResult, setUaeWaitingExamResult] = useState(null); // true = waiting, false = got results
  const [highSchoolCurriculum, setHighSchoolCurriculum] = useState(null);
  const [highSchoolGrade, setHighSchoolGrade] = useState(null);
  const [highSchoolYear, setHighSchoolYear] = useState(null);
  const [highSchoolName, setHighSchoolName] = useState('');
  const [qudrat, setQudrat] = useState('');
  const [tahsili, setTahsili] = useState('');
  const [hasEnglishCert, setHasEnglishCert] = useState(null); // true/false
  const [englishCertMarks, setEnglishCertMarks] = useState('');
  // Diploma Details State
  const [tasneefExpiry, setTasneefExpiry] = useState('');
  const [higherEducationProgram, setHigherEducationProgram] = useState('');
  const [higherEducationSchool, setHigherEducationSchool] = useState('');
  const [higherEducationGpa, setHigherEducationGpa] = useState('');
  const [noOfYears, setNoOfYears] = useState(null);
  const [certificateAttested, setCertificateAttested] = useState(null); // true/false
  // Select Major State
  const [pyMajor, setPyMajor] = useState(null);
  const [majorOptions, setMajorOptions] = useState([]);
  const [alternativeMajor, setAlternativeMajor] = useState(null);
  const [preferredProgram, setPreferredProgram] = useState('FAST');
  // Attachment State
  const [attachments, setAttachments] = useState({
    HIGH_SCHOOL_TRAN: null,
    QUDRAT: null,
    TAHSILI: null,
    LATEST_TRAN: null,
    ENGLISH_TEST: null,
    TASNEEF: null,
    GRAD_CERT: null
  });
  
  const datePickerRef = useRef(null);
  const dobPickerRef = useRef(null);
  const tasneefExpiryRef = useRef(null);
  const mobileInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const mobileItiRef = useRef(null);
  const phoneItiRef = useRef(null);
  
  const isRTL = i18n.language === 'ar' || i18n.language === 'ar-SA';

  // Reset form state when language changes
  useEffect(() => {
    setIntake(null);
    setIntakeOptions([]);
    setCampus(null);
    setCampusOptions([]);
    setAllIntakesBuffer([]);
    setLevel(null);
    // Keep other fields intact - don't reset everything, just language-dependent dropdowns
  }, [i18n.language]);

  // Reset session on component mount (page load/refresh)
  useEffect(() => {
    const resetSession = async () => {
      try {
        await fetch('/api/application/reset', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });
      } catch (error) {
        // Silent fail - not critical
      }
    };
    
    // Only run on client side
    if (typeof window !== 'undefined') {
      resetSession();
    }
  }, []); // Empty dependency - runs once on mount

  // Track step views when currentStep changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      trackStepView(currentStep);
      if (currentStep === 3) {
        trackFormComplete();
      }
    }
  }, [currentStep]);

  // Check if mobile OTP should be skipped based on Sitecore settings
  useEffect(() => {
    const checkSkipOtp = async () => {
      try {
        const country = site.isUAE ? 'AE' : 'SA';
        const response = await fetch(`/api/otp/skip-status?country=${country}`);
        const data = await response.json();
        if (data.success && data.data?.skipOtp) {
          setSkipMobileOtp(true);
          // Don't auto-verify - just hide the button, mobile field stays editable and required
        }
      } catch (error) {
        // Silent fail - keep OTP enabled if check fails
        console.error('Error checking skip OTP status:', error);
      }
    };
    
    if (typeof window !== 'undefined') {
      checkSkipOtp();
    }
  }, [site.isUAE]);

  // Initialize Flatpickr
  useEffect(() => {
    if (datePickerRef.current) {
      const fp = flatpickr(datePickerRef.current, {
        dateFormat: 'd/m/Y',
        minDate: 'today',
        position: isRTL ? 'above right' : 'above left',
        onChange: (selectedDates) => {
          setIdExpiry(selectedDates[0]);
        },
        locale: isRTL ? {
          firstDayOfWeek: 6,
          weekdays: {
            shorthand: ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'],
            longhand: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
          },
          months: {
            shorthand: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
            longhand: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
          }
        } : undefined
      });
      
      return () => fp.destroy();
    }
  }, [isRTL]);

  // Initialize Flatpickr for Date of Birth
  useEffect(() => {
    if (dobPickerRef.current) {
      const fp = flatpickr(dobPickerRef.current, {
        dateFormat: 'd/m/Y',
        maxDate: 'today',
        position: isRTL ? 'above right' : 'above left',
        onChange: (selectedDates) => {
          setDob(selectedDates[0]);
          // Validate age only when a date is selected
          if (selectedDates[0]) {
            validateAge(selectedDates[0]);
          }
        },
        locale: isRTL ? {
          firstDayOfWeek: 6,
          weekdays: {
            shorthand: ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'],
            longhand: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
          },
          months: {
            shorthand: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
            longhand: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
          }
        } : undefined
      });
      
      return () => fp.destroy();
    }
  }, [isRTL]);

  // Initialize Flatpickr for Tasneef Expiry Date
  useEffect(() => {
    if (tasneefExpiryRef.current) {
      const fp = flatpickr(tasneefExpiryRef.current, {
        dateFormat: 'd/m/Y',
        minDate: 'today',
        position: isRTL ? 'above right' : 'above left',
        onChange: (selectedDates) => {
          setTasneefExpiry(selectedDates[0]);
          setFieldErrors(prev => ({...prev, tasneefExpiry: ''}));
        },
        locale: isRTL ? {
          firstDayOfWeek: 6,
          weekdays: {
            shorthand: ['أحد', 'إثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'],
            longhand: ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت']
          },
          months: {
            shorthand: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
            longhand: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر']
          }
        } : undefined
      });
      
      return () => fp.destroy();
    }
  }, [isRTL, currentStep]); // Re-initialize when step changes to Step 3

  // Initialize intl-tel-input for mobile
  useEffect(() => {
    const initialCountryCode = site.isUAE ? 'ae' : 'sa';
    
    const loadIntlTelInput = () => {
      if (window.intlTelInput && mobileInputRef.current && !mobileItiRef.current) {
        mobileItiRef.current = window.intlTelInput(mobileInputRef.current, {
          initialCountry: initialCountryCode,
          separateDialCode: true,
          utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js'
        });
      }
    };

    if (window.intlTelInput) {
      loadIntlTelInput();
    } else {
      // Load intl-tel-input from CDN
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/css/intlTelInput.css';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/intlTelInput.min.js';
      script.onload = loadIntlTelInput;
      document.body.appendChild(script);
    }
    
    return () => {
      if (mobileItiRef.current) {
        mobileItiRef.current.destroy();
        mobileItiRef.current = null;
      }
    };
  }, [site.isUAE]);

  // Initialize intl-tel-input for phone
  useEffect(() => {
    const initialCountryCode = site.isUAE ? 'ae' : 'sa';
    
    const loadIntlTelInput = () => {
      if (window.intlTelInput && phoneInputRef.current && !phoneItiRef.current) {
        phoneItiRef.current = window.intlTelInput(phoneInputRef.current, {
          initialCountry: initialCountryCode,
          separateDialCode: true,
          utilsScript: 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js'
        });
      }
    };

    if (window.intlTelInput) {
      loadIntlTelInput();
    } else {
      // Load intl-tel-input from CDN
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/css/intlTelInput.css';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/intlTelInput.min.js';
      script.onload = loadIntlTelInput;
      document.body.appendChild(script);
    }
    
    return () => {
      if (phoneItiRef.current) {
        phoneItiRef.current.destroy();
        phoneItiRef.current = null;
      }
    };
  }, [site.isUAE]);

  // Fetch intake options when level changes - buffer all intakes and extract unique campuses
  useEffect(() => {
    if (!level) return;

    const fetchIntakeOptions = async () => {
      setIntakeLoading(true);
      setIntake(null); // Reset intake selection
      setCampus(null); // Reset campus selection
      setCampusOptions([]); // Reset campus options
      setIntakeOptions([]); // Reset intake options
      setAllIntakesBuffer([]); // Reset buffer
      
      try {
        const lang = isRTL ? 'ar_001' : 'en_US';
        const country = site.isUAE ? 'AE' : 'SA';
        
        // Use server-side proxy to avoid CORS
        const response = await fetch(`/api/odoo/intake?lang=${encodeURIComponent(lang)}&level=${encodeURIComponent(level.value)}&country=${encodeURIComponent(country)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch intakes: ${response.status}`);
        }

        const data = await response.json();
        
        if (data?.result?.data && Array.isArray(data.result.data)) {
          // Map all intakes and buffer them
          const allIntakes = data.result.data.map(item => ({
            value: item.id,
            label: item.name,
            campus: item.campus,
            level: item.level,
            application_type: item.application_type,
            programs: item.programs || []
          }));
          
          // Buffer all intakes for later filtering
          setAllIntakesBuffer(allIntakes);
          
          // Extract unique campus codes from intakes
          const uniqueCampusCodes = [...new Set(allIntakes.map(intake => intake.campus).filter(Boolean))];
          
          // Map campus codes to dropdown options with labels
          const campusOpts = uniqueCampusCodes.map(code => ({
            value: code,
            label: CAMPUS_MAPPING[code] ? (isRTL ? CAMPUS_MAPPING[code].ar : CAMPUS_MAPPING[code].en) : code
          }));
          
          setCampusOptions(campusOpts);
          
          // Don't set intakeOptions yet - wait for campus selection
        } else {
          setAllIntakesBuffer([]);
          setCampusOptions([]);
        }
      } catch (error) {
        console.error('Error fetching intake options:', error);
        setModalMessage({
          en: 'Failed to load intake options. Please try again.',
          ar: 'فشل تحميل خيارات القبول. الرجاء المحاولة مرة أخرى.'
        });
        setShowModal(true);
        setAllIntakesBuffer([]);
        setCampusOptions([]);
      } finally {
        setIntakeLoading(false);
      }
    };

    fetchIntakeOptions();
  }, [level, isRTL]);

  // Filter intakes when campus changes
  useEffect(() => {
    if (!campus || allIntakesBuffer.length === 0) {
      setIntakeOptions([]);
      return;
    }

    // Filter buffered intakes by selected campus
    const filteredIntakes = allIntakesBuffer.filter(intake => intake.campus === campus.value);
    setIntakeOptions(filteredIntakes);
    setIntake(null); // Reset intake selection when campus changes
    setPyMajor(null); // Reset major selection
    setMajorOptions([]); // Reset major options
  }, [campus, allIntakesBuffer]);

  // OTP Timer countdown
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpTimer]);

  // Focus first OTP input when modal opens
  useEffect(() => {
    if (showOtpModal && otpInputRefs.current[0]) {
      // Use setTimeout to ensure the modal is fully rendered
      setTimeout(() => {
        if (otpInputRefs.current[0]) {
          otpInputRefs.current[0].focus();
        }
      }, 100);
    }
  }, [showOtpModal]);

  // Format timer as mm:ss
  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle OTP digit input
  const handleOtpDigitChange = (index, value) => {
    // Only allow numeric input
    const digit = value.replace(/[^0-9]/g, '').slice(-1);
    const newDigits = [...otpDigits];
    newDigits[index] = digit;
    setOtpDigits(newDigits);
    setOtpError('');

    // Auto-focus next input
    if (digit && index < 5 && otpInputRefs.current[index + 1]) {
      otpInputRefs.current[index + 1].focus();
    }
  };

  // Handle OTP paste
  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (pastedData.length > 0) {
      const newDigits = [...otpDigits];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newDigits[i] = pastedData[i];
      }
      setOtpDigits(newDigits);
      setOtpError('');
      // Focus last filled or next empty
      const lastIndex = Math.min(pastedData.length, 5);
      if (otpInputRefs.current[lastIndex]) {
        otpInputRefs.current[lastIndex].focus();
      }
    }
  };

  // Handle OTP backspace
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1].focus();
    }
  };

  // Send Email OTP
  const sendEmailOtp = async () => {
    if (!validateEmail(email)) {
      return;
    }

    // Check if national ID is filled
    if (!nationalId || nationalId.trim() === '') {
      setModalType('warning');
      setModalMessage({
        en: 'Please enter your national ID',
        ar: 'الرجاء ادخال رقم الهوية'
      });
      setShowModal(true);
      return;
    }

    setEmailOtpSending(true);
    setOtpError('');

    try {
      // First, check if email is already registered in Odoo
      const checkResponse = await fetch('/api/odoo/validate-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: email.toLowerCase(), 
          national_id: nationalId.trim() 
        })
      });

      if (!checkResponse.ok) {
        setModalType('warning');
        setModalMessage({
          en: 'There is a technical issue. Please try again later.',
          ar: 'يوجد مشكلة تقنية ، الرجاء المحاولة في وقت لاحق'
        });
        setShowModal(true);
        setEmailOtpSending(false);
        return;
      }

      const checkData = await checkResponse.json();
      const statusCode = checkData?.result?.status_code;

      // If status_code is 409, email is already registered
      if (statusCode === 409) {
        setModalType('warning');
        setModalMessage({
          en: 'You already have a profile with the same email',
          ar: 'لديك تسجيل مسبق بهذا الايميل'
        });
        setShowModal(true);
        setEmailOtpSending(false);
        return;
      }

      // Email is not registered (status_code 200), proceed to send OTP
      const country = site.isUAE ? 'AE' : 'SA';
      const response = await fetch('/api/otp/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase(), country: country })
      });

      const data = await response.json();

      if (data.success) {
        setOtpType('email');
        setOtpDigits(['', '', '', '', '', '']);
        setOtpTimer(180); // 3 minutes
        setShowOtpModal(true);
      } else {
        // Handle errors
        if (data.error === 'ActiveWindow') {
          // Already have active OTP window
          setOtpType('email');
          setOtpDigits(['', '', '', '', '', '']);
          setOtpTimer(data.remaining || 180);
          setShowOtpModal(true);
        } else {
          setModalType('warning');
          setModalMessage({
            en: 'Failed to send verification code. Please try again.',
            ar: 'فشل إرسال رمز التحقق. الرجاء المحاولة مرة أخرى.'
          });
          setShowModal(true);
        }
      }
    } catch (error) {
      console.error('Error sending email OTP:', error);
      setModalType('warning');
      setModalMessage({
        en: 'There is a technical issue. Please try again later.',
        ar: 'يوجد مشكلة تقنية ، الرجاء المحاولة في وقت لاحق'
      });
      setShowModal(true);
    } finally {
      setEmailOtpSending(false);
    }
  };

  // Verify Email OTP
  const verifyEmailOtp = async () => {
    const otpCode = otpDigits.join('');
    if (otpCode.length !== 6) {
      setOtpError(isRTL ? 'أدخل الرمز المكون من 6 أرقام' : 'Enter the 6-digit code');
      return;
    }

    setOtpVerifying(true);
    setOtpError('');

    try {
      const response = await fetch('/api/otp/verify-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.toLowerCase(), otp: otpCode })
      });

      const data = await response.json();

      if (data.success) {
        setEMAIL_VER(true);
        setShowOtpModal(false);
        setOtpDigits(['', '', '', '', '', '']);
        setOtpTimer(0);
        setModalType('success');
        setModalMessage({
          en: 'Email verified successfully!',
          ar: 'تم التحقق من البريد الإلكتروني بنجاح!'
        });
        setShowModal(true);
      } else {
        // Handle verification errors
        if (data.error === 'InvalidCode') {
          setOtpError(isRTL 
            ? `رمز غير صحيح. المحاولات المتبقية: ${data.remaining}` 
            : `Invalid code. Remaining attempts: ${data.remaining}`);
        } else if (data.error === 'Expired') {
          setOtpError(isRTL ? 'انتهت صلاحية الرمز' : 'Code has expired');
          setOtpTimer(0);
        } else if (data.error === 'TooManyAttempts') {
          setOtpError(isRTL ? 'تجاوزت الحد الأقصى من المحاولات' : 'Too many attempts');
          setOtpTimer(0);
        } else {
          setOtpError(isRTL ? 'فشل التحقق' : 'Verification failed');
        }
      }
    } catch (error) {
      console.error('Error verifying email OTP:', error);
      setOtpError(isRTL ? 'خطأ في التحقق' : 'Verification error');
    } finally {
      setOtpVerifying(false);
    }
  };

  // Send Mobile OTP
  const sendMobileOtp = async () => {
    // Get full mobile number with country code
    let fullMobile = mobile;
    if (mobileItiRef.current) {
      fullMobile = mobileItiRef.current.getNumber();
    }

    if (!fullMobile || fullMobile.length < 8) {
      setMobileError(isRTL ? 'رقم الجوال غير صحيح' : 'Invalid mobile number');
      return;
    }

    // Check if mobile number is valid using intl-tel-input validation
    if (mobileItiRef.current && !mobileItiRef.current.isValidNumber()) {
      setMobileError(isRTL ? 'يرجى إدخال رقم هاتف صحيح' : 'Enter a valid phone number');
      return;
    }

    setMobileOtpSending(true);
    setOtpError('');

    try {
      const country = site.isUAE ? 'AE' : 'SA';
      const response = await fetch('/api/otp/send-mobile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: fullMobile, country: country })
      });

      const data = await response.json();

      if (data.success) {
        // Check if OTP is skipped
        if (data.skipOtp) {
          // Auto-verify without showing OTP modal
          setMOBILE_VER(true);
          setModalMessage({
            en: 'Mobile verified successfully!',
            ar: 'تم التحقق من الجوال بنجاح!'
          });
          setModalType('success');
          setShowModal(true);
        } else {
          setOtpType('mobile');
          setOtpDigits(['', '', '', '', '', '']);
          setOtpTimer(180);
          setShowOtpModal(true);
        }
      } else {
        if (data.error === 'ActiveWindow') {
          setOtpType('mobile');
          setOtpDigits(['', '', '', '', '', '']);
          setOtpTimer(data.remaining || 180);
          setShowOtpModal(true);
        } else {
          setModalMessage({
            en: 'Failed to send verification code. Please try again.',
            ar: 'فشل إرسال رمز التحقق. الرجاء المحاولة مرة أخرى.'
          });
          setShowModal(true);
        }
      }
    } catch (error) {
      console.error('Error sending mobile OTP:', error);
      setModalMessage({
        en: 'There is a technical issue. Please try again later.',
        ar: 'يوجد مشكلة تقنية ، الرجاء المحاولة في وقت لاحق'
      });
      setShowModal(true);
    } finally {
      setMobileOtpSending(false);
    }
  };

  // Verify Mobile OTP
  const verifyMobileOtp = async () => {
    const otpCode = otpDigits.join('');
    if (otpCode.length !== 6) {
      setOtpError(isRTL ? 'أدخل الرمز المكون من 6 أرقام' : 'Enter the 6-digit code');
      return;
    }

    let fullMobile = mobile;
    if (mobileItiRef.current) {
      fullMobile = mobileItiRef.current.getNumber();
    }

    setOtpVerifying(true);
    setOtpError('');

    try {
      const country = site.isUAE ? 'AE' : 'SA';
      const response = await fetch('/api/otp/verify-mobile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobile: fullMobile, otp: otpCode, country: country })
      });

      const data = await response.json();

      if (data.success) {
        setMOBILE_VER(true);
        setShowOtpModal(false);
        setOtpDigits(['', '', '', '', '', '']);
        setOtpTimer(0);
        setModalType('success');
        setModalMessage({
          en: 'Mobile verified successfully!',
          ar: 'تم التحقق من رقم الجوال بنجاح!'
        });
        setShowModal(true);
      } else {
        if (data.error === 'InvalidCode') {
          setOtpError(isRTL 
            ? `رمز غير صحيح. المحاولات المتبقية: ${data.remaining}` 
            : `Invalid code. Remaining attempts: ${data.remaining}`);
        } else if (data.error === 'Expired') {
          setOtpError(isRTL ? 'انتهت صلاحية الرمز' : 'Code has expired');
          setOtpTimer(0);
        } else if (data.error === 'TooManyAttempts') {
          setOtpError(isRTL ? 'تجاوزت الحد الأقصى من المحاولات' : 'Too many attempts');
          setOtpTimer(0);
        } else {
          setOtpError(isRTL ? 'فشل التحقق' : 'Verification failed');
        }
      }
    } catch (error) {
      console.error('Error verifying mobile OTP:', error);
      setOtpError(isRTL ? 'خطأ في التحقق' : 'Verification error');
    } finally {
      setOtpVerifying(false);
    }
  };

  // Resend OTP
  const resendOtp = () => {
    if (otpTimer > 0) return; // Cannot resend while timer active
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
    if (otpType === 'email') {
      sendEmailOtp();
    } else {
      sendMobileOtp();
    }
  };

  // Close OTP Modal
  const closeOtpModal = () => {
    setShowOtpModal(false);
    setOtpDigits(['', '', '', '', '', '']);
    setOtpError('');
  };

  // Default labels based on language
  const defaultLabels = isRTL 
    ? ['معلومات شخصية', 'معلومات تعليمية', 'شكرا لك']
    : ['Personal Information', 'Educational Information', 'Thank You'];

  const steps = [
    { 
      id: 1, 
      label: fields?.step1Label?.value || defaultLabels[0]
    },
    { 
      id: 2, 
      label: fields?.step2Label?.value || defaultLabels[1]
    },
    { 
      id: 3, 
      label: fields?.step3Label?.value || defaultLabels[2]
    }
  ];

  // Helper function to get site-specific accordion items
  // Filters accordion items based on the 'site' field (KSA, UAE, or ALL/empty)
  const getAccordionItems = () => {
    const allItems = fields?.step2AccordionItems || [];
    
    // Filter by site field if it exists
    return allItems.filter(item => {
      // Handle Droplink field structure: { id: "...", url: "/Content/Settings/SiteOptions/KSA", fields: {} }
      // Extract site name from the URL path
      const siteField = item?.fields?.site;
      let itemSite = '';
      
      if (typeof siteField === 'string') {
        itemSite = siteField;
      } else if (siteField?.url) {
        // Extract the last part of the URL path (e.g., "KSA" from "/Content/Settings/SiteOptions/KSA")
        const urlParts = siteField.url.split('/');
        itemSite = urlParts[urlParts.length - 1] || '';
      } else if (typeof siteField?.value === 'string') {
        itemSite = siteField.value;
      } else if (siteField?.value?.name) {
        itemSite = siteField.value.name;
      } else if (siteField?.name) {
        itemSite = siteField.name;
      } else if (siteField?.displayName) {
        itemSite = siteField.displayName;
      }
      
      // Show if: no site specified, site is 'ALL', or matches current site
      if (!itemSite || itemSite.toUpperCase() === 'ALL') return true;
      return itemSite.toUpperCase() === site.country;
    });
  };

  // Helper function to get site-specific level options
  // Filters level items based on the 'site' field (KSA, UAE, or ALL/empty)
  const getLevelOptions = () => {
    const allLevels = fields?.levelOptions || [];
    
    // If no levels from Sitecore, return hardcoded fallback
    if (allLevels.length === 0) {
      return [
        { value: 'diploma', label: isRTL ? 'دبلوم' : 'Diploma' },
        { value: 'sci_fnd', label: isRTL ? 'السنة التحضيرية لطلاب الثانوية غير العلمية' : 'Science Foundation' },
        { value: 'bachelor', label: isRTL ? 'بكالوريس' : 'Bachelor' },
        { value: 'master', label: isRTL ? 'ماجستير' : 'Master' }
      ];
    }
    
    // Filter by site field and map to dropdown format
    return allLevels.filter(item => {
      const siteField = item?.fields?.site;
      let itemSite = '';
      
      if (typeof siteField === 'string') {
        itemSite = siteField;
      } else if (siteField?.url) {
        const urlParts = siteField.url.split('/');
        itemSite = urlParts[urlParts.length - 1] || '';
      } else if (typeof siteField?.value === 'string') {
        itemSite = siteField.value;
      } else if (siteField?.value?.name) {
        itemSite = siteField.value.name;
      } else if (siteField?.name) {
        itemSite = siteField.name;
      } else if (siteField?.displayName) {
        itemSite = siteField.displayName;
      }
      
      // Show if: no site specified, site is 'ALL', or matches current site
      if (!itemSite || itemSite.toUpperCase() === 'ALL') return true;
      return itemSite.toUpperCase() === site.country;
    }).map(item => {
      // Safely extract string values from Sitecore fields
      const getValue = (field) => {
        if (!field) return '';
        if (typeof field === 'string') return field;
        if (typeof field?.value === 'string') return field.value;
        return '';
      };
      
      const valueField = getValue(item?.fields?.value) || item?.name || '';
      const labelField = getValue(item?.fields?.label) || item?.name || '';
      const labelArField = getValue(item?.fields?.labelAr) || labelField;
      
      return {
        value: valueField,
        label: isRTL ? labelArField : labelField
      };
    });
  };

  // Required field validation messages
  const requiredFieldMessage = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';

  // Validate Tab 1 required fields
  const validateTab1Fields = () => {
    const errors = {};
    
    if (!level) errors.level = requiredFieldMessage;
    if (!campus) errors.campus = requiredFieldMessage;
    if (!intake) errors.intake = requiredFieldMessage;
    if (!nationalId) errors.nationalId = requiredFieldMessage;
    if (residencyStatus !== 'saudi_citizen' && residencyStatus !== 'emirati_citizen' && !nationality) errors.nationality = requiredFieldMessage;
    if (!gender) errors.gender = requiredFieldMessage;
    if (!firstNameEn || !firstNameEn.trim()) errors.firstNameEn = requiredFieldMessage;
    if (!secondNameEn || !secondNameEn.trim()) errors.secondNameEn = requiredFieldMessage;
    if (!lastNameEn || !lastNameEn.trim()) errors.lastNameEn = requiredFieldMessage;
    if (!email) errors.email = requiredFieldMessage;
    if (!mobile) errors.mobile = requiredFieldMessage;
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Format date for API (keep as dd/mm/yyyy)
  const formatDateForApi = (dateStr) => {
    if (!dateStr) return '';
    // If it's already a Date object
    if (dateStr instanceof Date) {
      const day = String(dateStr.getDate()).padStart(2, '0');
      const month = String(dateStr.getMonth() + 1).padStart(2, '0');
      const year = dateStr.getFullYear();
      return `${day}/${month}/${year}`;
    }
    // If it's a string in dd/mm/yyyy format, return as-is
    if (typeof dateStr === 'string' && dateStr.includes('/')) {
      return dateStr;
    }
    // If it's in yyyy-mm-dd format, convert to dd/mm/yyyy
    if (typeof dateStr === 'string' && dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  // Submit Tab 1 data
  const submitTab1 = async () => {
    try {
      // Get mobile number with country code
      let mobileNumber = mobile;
      if (mobileItiRef.current) {
        mobileNumber = mobileItiRef.current.getNumber();
      }

      // Get phone number with country code
      let phoneNumber = phone;
      if (phoneItiRef.current && phone) {
        phoneNumber = phoneItiRef.current.getNumber();
      }

      // Get gclid from localStorage
      let gclidValue = null;
      try {
        const raw = localStorage.getItem('gclid');
        if (raw) {
          const obj = JSON.parse(raw);
          gclidValue = obj.value || null;
        }
      } catch (e) {
        console.error('Could not parse gclid JSON:', e);
      }

      // Extract Google Analytics Client ID from _ga cookie
      let googleCid = null;
      try {
        const gaCookie = document.cookie.split('; ').find(row => row.startsWith('_ga='));
        if (gaCookie) {
          googleCid = gaCookie.split('=')[1].split('.').slice(2).join('.');
        }
      } catch (e) {
        console.error('Could not extract google_cid:', e);
      }

      // Extract Google Analytics Session ID from _ga_* cookie
      let googleSid = null;
      try {
        const sessionCookie = document.cookie.split('; ').find(row => row.startsWith('_ga_'));
        if (sessionCookie) {
          const sidMatch = sessionCookie.match(/s(\d+)/);
          googleSid = sidMatch ? sidMatch[1] : null;
        }
      } catch (e) {
        console.error('Could not extract google_sid:', e);
      }

      const sanitizeName = (name) =>
        (name || '').split(/\s+/).filter(w => !/admissionbmc/i.test(w)).join(' ').trim();

      const requestBody = {
        lang: isRTL ? 'ar_001' : 'en_US',
        first_name_en: sanitizeName(firstNameEn),
        second_name_en: sanitizeName(secondNameEn),
        third_name_en: sanitizeName(thirdNameEn),
        last_name_en: sanitizeName(lastNameEn),
        intake_id: intake?.value ? parseInt(intake.value, 10) : 0,
        nationality: residencyStatus === 'saudi_citizen' ? 'SA' : residencyStatus === 'emirati_citizen' ? 'AE' : (nationality?.value || ''),
        mobile: mobileNumber,
        email: email,
        phone: phoneNumber || '',
        gender: gender,
        national_id: nationalId,
        residency_status: residencyStatus,
        site_country: site.isUAE ? 'AE' : 'SA',
        gclid: gclidValue,
        google_cid: googleCid,
        google_sid: googleSid
      };

      const odooUrl = '/api/application/create-app';
      console.log('=== STEP 1 SUBMISSION ===' );
      console.log('Endpoint Hit:', odooUrl);
      console.log('Request Payload:', requestBody);
      
      const response = await fetch(odooUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('Controller Response:', data);
      console.log('============================');
      return data;
    } catch (error) {
      console.error('Tab1 submit error:', error);
      return { success: false, error: 'network_error' };
    }
  };

  const handleNext = async () => {
    // Track button click with field values - Next for step 1, Submit for step 2
    if (currentStep === 1) {
      trackNextClick(currentStep, {
        'Choose Level': level?.label || '',
        'Choose Campus': campus?.label || '',
        'Choose Intake': intake?.label || '',
        'Residency Status': residencyStatus || ''
      });
    } else if (currentStep === 2) {
      trackSubmitClick(currentStep, {
        // Step 1 fields
        'Choose Level': level?.label || '',
        'Choose Campus': campus?.label || '',
        'Choose Intake': intake?.label || '',
        'Residency Status': residencyStatus || '',
        // Step 2 fields
        'High School Curriculum': highSchoolCurriculum?.label || '',
        'High School Grades': highSchoolGrade?.label || '',
        'GPA': higherEducationGpa || '',
        'Main Major': pyMajor?.label || ''
      });
    }
    // Scroll to top of page
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Step 1 submission
    if (currentStep === 1) {
      // Client-side required field validation
      if (!validateTab1Fields()) {
        setModalType('warning');
        setModalMessage({
          en: 'Please fill in all required fields.',
          ar: 'يرجى ملء جميع الحقول المطلوبة.'
        });
        setShowModal(true);
        return;
      }

      // Check second mobile (phone) does not duplicate the primary mobile
      if (phone && phoneItiRef.current && mobileItiRef.current) {
        const phoneNumber = phoneItiRef.current.getNumber();
        const mobileNumber = mobileItiRef.current.getNumber();
        if (phoneNumber && phoneNumber === mobileNumber) {
          setPhoneError(isRTL ? 'يرجى إدخال رقم هاتف آخر' : 'Enter different phone number');
          return;
        }
      }

      // Check if rules are accepted
      if (!rulesAccepted) {
        setModalType('warning');
        setModalMessage({
          en: 'Please accept the Admission & Registration Rules.',
          ar: 'الرجاء الموافقة على لوائح وشروط القبول والتسجيل.'
        });
        setShowModal(true);
        return;
      }

      // Client-side OTP verification check (quick feedback)
      if (!EMAIL_VER) {
        setModalType('warning');
        setModalMessage({
          en: 'Please verify your email address before proceeding.',
          ar: 'يرجى التحقق من بريدك الإلكتروني قبل المتابعة.'
        });
        setShowModal(true);
        return;
      }
      if (!MOBILE_VER && !skipMobileOtp) {
        setModalType('warning');
        setModalMessage({
          en: 'Please verify your mobile number before proceeding.',
          ar: 'يرجى التحقق من رقم جوالك قبل المتابعة.'
        });
        setShowModal(true);
        return;
      }

      // Submit to server
      setIsSubmitting(true);
      
      try {
        const result = await submitTab1();

        if (result.success) {
          // Store application ID and proceed
          setApplicationId(result.data?.application_id);
          setCurrentStep(2);
          // Scroll to top after step change
          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        } else {
          // Handle error
          setModalType('warning');
          setModalMessage({
            en: result.message_en || 'We apologize, but something went wrong. Please try again later. If the issue persists, contact our support team.',
            ar: result.message_ar || 'نعتذر، ولكن حدث خطأ ما. يرجى المحاولة مرة أخرى لاحقًا. إذا استمرت المشكلة، فاتصل بمركز خدمة العملاء.'
          });
          setShowModal(true);
        }
      } catch (error) {
        setModalType('warning');
        setModalMessage({
          en: 'We apologize, but something went wrong. Please try again later. If the issue persists, contact our support team.',
          ar: 'نعتذر، ولكن حدث خطأ ما. يرجى المحاولة مرة أخرى لاحقًا. إذا استمرت المشكلة، فاتصل بمركز خدمة العملاء.'
        });
        setShowModal(true);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    
    if (currentStep === 2) {
      // Check application types
      const isHighSchool = intake?.application_type === 'high_school' || intake?.application_type === 'academic_acceleration';
      const isAcademicAcceleration = intake?.application_type === 'academic_acceleration';
      const isAcceleration = intake?.level === 'acceleration' && intake?.application_type === 'high_school';
      const isBridging = intake?.application_type === 'bridging';
      const isTransfer = intake?.application_type === 'transfer';
      const isDoubleMajor = intake?.application_type === 'double_major';
      const isMaster = intake?.application_type === 'master';
      const isSciFnd = intake?.level === 'sci_fnd' && intake?.application_type === 'high_school';
      const isDiploma = intake?.level === 'diploma' && intake?.application_type === 'high_school';
      // UAE high_school: simplified form - hide exam result, qudrat, tahsili, english cert, alternative major, etc.
      const isUaeHighSchool = site.isUAE && intake?.application_type === 'high_school' && !isAcademicAcceleration;
      
      // Check if same major should be allowed: high_school with 2 programs (one is MEDI) and restricted grades
      const restrictedGradesForSameMajor = highSchoolCurriculum?.value === 'international_high_school'
        ? ['80', '60', '59', 'B', 'C', 'D', 'E']
        : ['90', '80', '60', '59', 'B', 'C', 'D', 'E'];
      const hasMediProgram = majorOptions.some(opt => opt.value === 'MEDI');
      const allowSameMajor = intake?.application_type === 'high_school' && 
                             majorOptions.length === 2 && 
                             hasMediProgram && 
                             restrictedGradesForSameMajor.includes(highSchoolGrade?.value);
      
      // Validate that main major and alternative major are not the same (skip for bridging, transfer, double_major, master, sci_fnd, diploma, acceleration, when only one option available, or when allowSameMajor is true)
      if (!allowSameMajor && majorOptions.length > 1 && !isBridging && !isTransfer && !isDoubleMajor && !isMaster && !isSciFnd && !isDiploma && !isAcceleration && alternativeMajor && pyMajor && alternativeMajor.value === pyMajor.value) {
        setModalType('warning');
        setModalMessage({
          en: 'The selected main major and alternative major must not be the same',
          ar: 'يجب ألا يكون التخصص الرئيسي المختار والتخصص البديل متطابقين'
        });
        setShowModal(true);
        return;
      }
      
      // Validate required fields for Step 3
      let hasErrors = false;
      const newFieldErrors = { ...fieldErrors };
      
      // High School Curriculum and Grade - not required for master, required for sci_fnd
      if (!isMaster && !highSchoolCurriculum) {
        newFieldErrors.highSchoolCurriculum = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
        hasErrors = true;
      }
      if (!isMaster && !highSchoolGrade) {
        newFieldErrors.highSchoolGrade = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
        hasErrors = true;
      }
      // Exam result not required for bridging, transfer, double_major, master, UAE high_school, sci_fnd
      // Only required when Saudi High School curriculum is selected (not UAE high school)
      if (!isUaeHighSchool && !isSciFnd && !isBridging && !isTransfer && !isDoubleMajor && !isMaster && (highSchoolCurriculum?.value === 'saudi_high_school' || highSchoolCurriculum?.value === 'emirati_high_school') && (examResult === null || examResult === undefined)) {
        newFieldErrors.examResult = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
        hasErrors = true;
      }
      if (!pyMajor) {
        newFieldErrors.pyMajor = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
        hasErrors = true;
      }
      // KSA high_school/academic_acceleration MEDI restriction - prevent submission if MEDI selected with restricted grades
      const restrictedGrades = highSchoolCurriculum?.value === 'international_high_school'
        ? ['80', '60', '59', 'B', 'C', 'D', 'E']
        : ['90', '80', '60', '59', 'B', 'C', 'D', 'E'];
      if (!site.isUAE && (intake?.application_type === 'high_school' || intake?.application_type === 'academic_acceleration') && restrictedGrades.includes(highSchoolGrade?.value)) {
        if (pyMajor?.value === 'MEDI') {
          newFieldErrors.pyMajor = isRTL ? 'غير مؤهل لبرنامج الطب' : 'Not eligible for Medicine Program';
          hasErrors = true;
          setModalType('warning');
          setModalMessage({
            en: 'Dear Applicant,<br/><br/>We would like to inform you that you are not currently eligible for admission to the Medicine Program based on the admission criteria. You may enroll in an alternative academic program, with the opportunity to transfer to the General Medicine program upon achieving a GPA of 4.5 in the first year, in accordance with the approved policy.<br/><br/>We wish you success in your academic journey.<br/><br/><a href="https://bmc.edu.sa/en/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">Check the admission criteria</a>',
            ar: 'عزيزي المتقدم،<br/><br/>نود إفادتك بأنك غير مؤهل حاليًا للقبول في البرنامج الطب العام وفقاً لمعايير القبول. يمكنك الالتحاق ببرنامج أكاديمي آخر مع إمكانية التحويل إلى برنامج الطب العام عند تحقيقك معدل (4.5) في السنة الاولى وفق السياسة المعتمدة.<br/><br/>نتمنى لك التوفيق في رحلتك الأكاديمية<br/><br/><a href="https://bmc.edu.sa/ar/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">تحقق من معايير القبول</a>'
          });
          setShowModal(true);
        }
        if (alternativeMajor?.value === 'MEDI' && !isAcceleration) {
          newFieldErrors.alternativeMajor = isRTL ? 'غير مؤهل لبرنامج الطب' : 'Not eligible for Medicine Program';
          hasErrors = true;
          if (pyMajor?.value !== 'MEDI') {
            setModalType('warning');
            setModalMessage({
              en: 'Dear Applicant,<br/><br/>We would like to inform you that you are not currently eligible for admission to the Medicine Program based on the admission criteria. You may enroll in an alternative academic program, with the opportunity to transfer to the General Medicine program upon achieving a GPA of 4.5 in the first year, in accordance with the approved policy.<br/><br/>We wish you success in your academic journey.<br/><br/><a href="https://bmc.edu.sa/en/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">Check the admission criteria</a>',
              ar: 'عزيزي المتقدم،<br/><br/>نود إفادتك بأنك غير مؤهل حاليًا للقبول في البرنامج الطب العام وفقاً لمعايير القبول. يمكنك الالتحاق ببرنامج أكاديمي آخر مع إمكانية التحويل إلى برنامج الطب العام عند تحقيقك معدل (4.5) في السنة الاولى وفق السياسة المعتمدة.<br/><br/>نتمنى لك التوفيق في رحلتك الأكاديمية<br/><br/><a href="https://bmc.edu.sa/ar/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">تحقق من معايير القبول</a>'
            });
            setShowModal(true);
          }
        }
      }
      // Alternative Major not required for bridging, transfer, double_major, master, sci_fnd, diploma, acceleration, or UAE high_school
      if (!isUaeHighSchool && !isBridging && !isTransfer && !isDoubleMajor && !isMaster && !isSciFnd && !isDiploma && !isAcceleration && !alternativeMajor) {
        newFieldErrors.alternativeMajor = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
        hasErrors = true;
      }
      
      // Qudrat and Tahsili required if examResult is No (false) - not for bridging, transfer, double_major, master, UAE high_school, or sci_fnd
      // Only applies when Saudi High School curriculum is selected (not UAE high school)
      if (!isUaeHighSchool && !isSciFnd && !isBridging && !isTransfer && !isDoubleMajor && !isMaster && (highSchoolCurriculum?.value === 'saudi_high_school' || highSchoolCurriculum?.value === 'emirati_high_school') && examResult === false) {
        if (!qudrat) {
          newFieldErrors.qudrat = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
        if (!tahsili) {
          newFieldErrors.tahsili = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
      }
      
      // Fields required for bridging, transfer, double_major, master, academic_acceleration and non-high_school applicants (NOT for sci_fnd or diploma)
      if ((isBridging || isTransfer || isDoubleMajor || isMaster || isAcademicAcceleration || !isHighSchool) && !isSciFnd && !isDiploma) {
        if (hasEnglishCert === null || hasEnglishCert === undefined) {
          newFieldErrors.hasEnglishCert = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
        if (hasEnglishCert === true && (!englishCertMarks || !englishCertMarks.trim())) {
          newFieldErrors.englishCertMarks = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
        // Skip other diploma fields for academic_acceleration
        if (!isAcademicAcceleration) {
          if (!higherEducationProgram || !higherEducationProgram.trim()) {
            newFieldErrors.higherEducationProgram = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
            hasErrors = true;
          }
          // University/College not required for bridging
            if (!isBridging && (!higherEducationSchool || !higherEducationSchool.trim())) {
            newFieldErrors.higherEducationSchool = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
            hasErrors = true;
          }
          if (!higherEducationGpa) {
            newFieldErrors.higherEducationGpa = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
            hasErrors = true;
          }
          // No of years not required for transfer, double_major, or master
          if (!isTransfer && !isDoubleMajor && !isMaster && !noOfYears) {
            newFieldErrors.noOfYears = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
            hasErrors = true;
          }
        }
      }
      
      // Validate required attachments
      // High School Transcript - required except for master and UAE high_school (UAE has separate logic)
      if (!isMaster && !isUaeHighSchool && !attachments.HIGH_SCHOOL_TRAN) {
        newFieldErrors.HIGH_SCHOOL_TRAN = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
        hasErrors = true;
      }
      
      // Qudrat and Tahsili certificates - required if examResult is No (not for bridging, transfer, double_major, master, UAE high_school, or sci_fnd)
      // Only applies when Saudi High School curriculum is selected (not UAE high school)
      if (!isUaeHighSchool && !isSciFnd && !isBridging && !isTransfer && !isDoubleMajor && !isMaster && (highSchoolCurriculum?.value === 'saudi_high_school' || highSchoolCurriculum?.value === 'emirati_high_school') && examResult === false) {
        if (!attachments.QUDRAT) {
          newFieldErrors.QUDRAT = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
        if (!attachments.TAHSILI) {
          newFieldErrors.TAHSILI = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
      }
      
      // Attachments required for bridging, transfer, double_major, master, academic_acceleration, and non-high_school applicants
      if (isBridging || isTransfer || isDoubleMajor || isMaster || isAcademicAcceleration || !isHighSchool) {
        // Skip LATEST_TRAN, TASNEEF, GRAD_CERT for academic_acceleration (only needs English test if hasEnglishCert)
        if (!isAcademicAcceleration) {
          if (!attachments.LATEST_TRAN) {
            newFieldErrors.LATEST_TRAN = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
            hasErrors = true;
          }
        }
        // English test: for bridging/transfer/double_major/master/academic_acceleration only required if hasEnglishCert, for others always required
        if (isBridging || isTransfer || isDoubleMajor || isMaster || isAcademicAcceleration) {
          if (hasEnglishCert === true && !attachments.ENGLISH_TEST) {
            newFieldErrors.ENGLISH_TEST = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
            hasErrors = true;
          }
        } else if (!isHighSchool) {
          if (!attachments.ENGLISH_TEST) {
            newFieldErrors.ENGLISH_TEST = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
            hasErrors = true;
          }
        }
        // Tasneef not required for transfer, double_major, master, academic_acceleration, or UAE bridging
        if (!isTransfer && !isDoubleMajor && !isMaster && !isAcademicAcceleration && !(isBridging && site.isUAE) && !attachments.TASNEEF) {
          newFieldErrors.TASNEEF = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
        // GRAD_CERT not required for academic_acceleration
        if (!isAcademicAcceleration && !attachments.GRAD_CERT) {
          newFieldErrors.GRAD_CERT = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
      }
      
      // UAE high_school: validate waiting exam result radio and corresponding attachment
      if (isUaeHighSchool) {
        if (uaeWaitingExamResult === null) {
          newFieldErrors.uaeWaitingExamResult = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        } else if (uaeWaitingExamResult === false && !attachments.GRAD_CERT) {
          // Not waiting - requires Graduation Certificate
          newFieldErrors.GRAD_CERT = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        } else if (uaeWaitingExamResult === true && !attachments.HIGH_SCHOOL_TRAN) {
          // Waiting - requires High School Transcript
          newFieldErrors.HIGH_SCHOOL_TRAN = isRTL ? 'هذا الحقل مطلوب' : 'This field is required';
          hasErrors = true;
        }
      }
      
      if (hasErrors) {
        setFieldErrors(newFieldErrors);
        setModalType('warning');
        setModalMessage({
          en: 'Please fill in all required fields',
          ar: 'يرجى ملء جميع الحقول المطلوبة'
        });
        setShowModal(true);
        return;
      }

      setIsSubmitting(true);
      try {
        // Prepare documents array - filter based on what should actually be submitted
        const documents = Object.entries(attachments)
          .filter(([key, file]) => {
            if (!file) return false;
            // For UAE high_school, only include the relevant attachment based on waiting status
            if (isUaeHighSchool) {
              if (uaeWaitingExamResult === true && key === 'GRAD_CERT') return false; // Waiting - don't send GRAD_CERT
              if (uaeWaitingExamResult === false && key === 'HIGH_SCHOOL_TRAN') return false; // Got results - don't send HIGH_SCHOOL_TRAN
            }
            // For non-Saudi/Emirati curriculum (International), exclude QUDRAT and TAHSILI
            if (highSchoolCurriculum?.value !== 'saudi_high_school' && highSchoolCurriculum?.value !== 'emirati_high_school') {
              if (key === 'QUDRAT' || key === 'TAHSILI') return false;
            }
            // For Saudi/Emirati high school, exclude QUDRAT and TAHSILI if examResult is true (waiting for results)
            if (!isUaeHighSchool && (highSchoolCurriculum?.value === 'saudi_high_school' || highSchoolCurriculum?.value === 'emirati_high_school') && examResult === true) {
              if (key === 'QUDRAT' || key === 'TAHSILI') return false;
            }
            // Exclude ENGLISH_TEST if hasEnglishCert is false (user selected 'No')
            if (hasEnglishCert === false && key === 'ENGLISH_TEST') return false;
            return true;
          })
          .map(([key, file]) => {
            return new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const base64 = reader.result.split(',')[1];
                resolve({
                  document_type_id: key,
                  file_name: file.name,
                  document_file: base64
                });
              };
              reader.onerror = reject;
              reader.readAsDataURL(file);
            });
          });

        const documentsArray = await Promise.all(documents);

        // Prepare payload based on application type
        // For UAE high_school: push null/false for hidden fields, push pyMajor as preferred_program
        const payload = {
          application_id: applicationId,
          high_school_curriculum: isMaster ? null : (highSchoolCurriculum?.value || ''),
          high_school_grade: isMaster ? null : (highSchoolGrade?.value || ''),
          qudrat: isUaeHighSchool || isSciFnd || (isHighSchool && examResult === true) || isBridging || isTransfer || isDoubleMajor || isMaster || (highSchoolCurriculum?.value !== 'saudi_high_school' && highSchoolCurriculum?.value !== 'emirati_high_school') ? null : qudrat,
          tahsili: isUaeHighSchool || isSciFnd || (isHighSchool && examResult === true) || isBridging || isTransfer || isDoubleMajor || isMaster || (highSchoolCurriculum?.value !== 'saudi_high_school' && highSchoolCurriculum?.value !== 'emirati_high_school') ? null : tahsili,
          higher_education_program: isUaeHighSchool || (isHighSchool && !isBridging && !isTransfer && !isDoubleMajor && !isMaster) || isSciFnd || isDiploma ? null : higherEducationProgram,
          higher_education_gpa: isUaeHighSchool || (isHighSchool && !isBridging && !isTransfer && !isDoubleMajor && !isMaster) || isSciFnd || isDiploma ? null : higherEducationGpa,
          higher_education_school: isUaeHighSchool || (isHighSchool && !isTransfer && !isDoubleMajor && !isMaster) || isBridging || isSciFnd || isDiploma ? null : higherEducationSchool,
          has_english_cert: isUaeHighSchool || (isHighSchool && !isAcademicAcceleration && !isBridging && !isTransfer && !isDoubleMajor && !isMaster) || isSciFnd || isDiploma ? false : hasEnglishCert,
          no_of_years: isUaeHighSchool || (isHighSchool && !isBridging) || isTransfer || isDoubleMajor || isMaster || isSciFnd || isDiploma ? null : (noOfYears?.value || ''),
          english_cert_marks: isUaeHighSchool || (isHighSchool && !isAcademicAcceleration && !isBridging && !isTransfer && !isDoubleMajor && !isMaster) || isSciFnd || isDiploma ? null : englishCertMarks,
          exam_result: isUaeHighSchool ? (uaeWaitingExamResult === true) : (isSciFnd || isBridging || isTransfer || isDoubleMajor || isMaster || (highSchoolCurriculum?.value !== 'saudi_high_school' && highSchoolCurriculum?.value !== 'emirati_high_school') ? false : examResult),
          preferred_program: isUaeHighSchool ? (pyMajor?.value || '') : (isSciFnd ? (pyMajor?.value || '') : (isDiploma ? (pyMajor?.value || '') : (isAcceleration ? (pyMajor?.value || '') : (isHighSchool ? 'FAST' : ((isBridging || isTransfer || isDoubleMajor || isMaster) ? (pyMajor?.value || '') : preferredProgram))))),
          py_major: isUaeHighSchool || isBridging || isTransfer || isDoubleMajor || isMaster || isSciFnd || isDiploma ? null : (pyMajor?.value || ''),
          alternative_major: isUaeHighSchool || isBridging || isTransfer || isDoubleMajor || isMaster || isSciFnd || isDiploma || isAcceleration ? null : (alternativeMajor?.value || ''),
          preferred_shift: 'morning',
          documents: documentsArray
        };

        const postUrl = '/api/application/update-app';
        console.log('=== STEP 3 SUBMISSION ===' );
        console.log('Endpoint Hit:', postUrl);
        console.log('Request Payload:', payload);
        
        const response = await fetch(postUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        const result = await response.json();
        console.log('Controller Response:', result);
        console.log('============================');

        if (result?.success && result?.data?.status_code === 200) {
          setCurrentStep(steps.length); // Go to last step
          // Scroll to top after step change
          setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        } else {
          setModalType('warning');
          setModalMessage({
            en: 'We apologize, but something went wrong. Please try again later. If the issue persists, contact our support team.',
            ar: 'نعتذر، ولكن حدث خطأ ما. يرجى المحاولة مرة أخرى لاحقًا. إذا استمرت المشكلة، فاتصل بمركز خدمة العملاء.'
          });
          setShowModal(true);
        }
      } catch (error) {
        setModalType('warning');
        setModalMessage({
          en: 'We apologize, but something went wrong. Please try again later. If the issue persists, contact our support team.',
          ar: 'نعتذر، ولكن حدث خطأ ما. يرجى المحاولة مرة أخرى لاحقًا. إذا استمرت المشكلة، فاتصل بمركز خدمة العملاء.'
        });
        setShowModal(true);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
      // Scroll to top after step change
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
    }
  };

  // Validation functions for Civil Registry
  const validateNationalId = (value) => {
    // Remove any non-digit characters for validation
    const digitsOnly = value.replace(/\D/g, '');
    
    if (residencyStatus === 'saudi_citizen') {
      if (!digitsOnly.startsWith('1') || digitsOnly.length !== 10) {
        setModalMessage({
          en: 'The National ID must start with the number 1 and consist of exactly 10 digits',
          ar: 'يجب أن يبدأ رقم الهوية بالرقم 1 وأن يتكوّن من 10 أرقام'
        });
        setShowModal(true);
        return false;
      }
    } else if (residencyStatus === 'emirati_citizen') {
      if (!digitsOnly.startsWith('7') || digitsOnly.length !== 15) {
        setModalMessage({
          en: 'The Emirates ID must start with the number 7 and consist of exactly 15 digits',
          ar: 'يجب أن يبدأ رقم الهوية الإماراتية بالرقم 7 وأن يتكوّن من 15 رقمًا'
        });
        setShowModal(true);
        return false;
      }
    } else if (residencyStatus === 'residency_holder') {
      // UAE residency holders: 15 digits starting with 7
      // KSA residency holders (Iqama): 10 digits starting with 2
      if (site.isUAE) {
        if (!digitsOnly.startsWith('7') || digitsOnly.length !== 15) {
          setModalMessage({
            en: 'The UAE Residency ID must start with the number 7 and consist of exactly 15 digits',
            ar: 'يجب أن يبدأ رقم الإقامة الإماراتية بالرقم 7 وأن يتكوّن من 15 رقمًا'
          });
          setShowModal(true);
          return false;
        }
      } else {
        if (!digitsOnly.startsWith('2') || digitsOnly.length !== 10) {
          setModalMessage({
            en: 'The Iqama ID must start with the number 2 and consist of exactly 10 digits',
            ar: 'يجب أن يبدأ رقم الإقامة بالرقم 2 وأن يتكوّن من 10 أرقام'
          });
          setShowModal(true);
          return false;
        }
      }
    }
    // International applicant: no validation
    return true;
  };

  const handleNationalIdChange = (e) => {
    const value = e.target.value;
    
    if (residencyStatus === 'international_applicant') {
      // Allow letters and numbers
      setNationalId(value);
    } else {
      // Allow numbers only
      const numbersOnly = value.replace(/\D/g, '');
      setNationalId(numbersOnly);
    }
  };

  const handleNationalIdKeyDown = (e) => {
    // Prevent Tab and Enter from leaving the field if validation would fail
    if ((e.key === 'Tab' || e.key === 'Enter') && nationalId) {
      const digitsOnly = nationalId.replace(/\D/g, '');
      
      let isValid = true;
      if (residencyStatus === 'saudi_citizen') {
        isValid = digitsOnly.startsWith('1') && digitsOnly.length === 10;
      } else if (residencyStatus === 'emirati_citizen') {
        isValid = digitsOnly.startsWith('7') && digitsOnly.length === 15;
      } else if (residencyStatus === 'residency_holder') {
        if (site.isUAE) {
          isValid = digitsOnly.startsWith('7') && digitsOnly.length === 15;
        } else {
          isValid = digitsOnly.startsWith('2') && digitsOnly.length === 10;
        }
      }
      
      if (!isValid) {
        e.preventDefault();
        e.stopPropagation();
        // Show validation message
        if (residencyStatus === 'saudi_citizen') {
          setModalMessage({
            en: 'The National ID must start with the number 1 and consist of exactly 10 digits',
            ar: 'يجب أن يبدأ رقم الهوية بالرقم 1 وأن يتكوّن من 10 أرقام'
          });
        } else if (residencyStatus === 'emirati_citizen') {
          setModalMessage({
            en: 'The Emirates ID must start with the number 7 and consist of exactly 15 digits',
            ar: 'يجب أن يبدأ رقم الهوية الإماراتية بالرقم 7 وأن يتكوّن من 15 رقمًا'
          });
        } else if (residencyStatus === 'residency_holder') {
          if (site.isUAE) {
            setModalMessage({
              en: 'The UAE Residency ID must start with the number 7 and consist of exactly 15 digits',
              ar: 'يجب أن يبدأ رقم الإقامة الإماراتية بالرقم 7 وأن يتكوّن من 15 رقمًا'
            });
          } else {
            setModalMessage({
              en: 'The Iqama ID must start with the number 2 and consist of exactly 10 digits',
              ar: 'يجب أن يبدأ رقم الإقامة بالرقم 2 وأن يتكوّن من 10 أرقام'
            });
          }
        }
        setShowModal(true);
        // Refocus immediately
        if (nationalIdInputRef.current) {
          nationalIdInputRef.current.focus();
        }
      }
    }
  };

  const handleNationalIdBlur = async (e) => {
    if (!nationalId) {
      setNationalIdValid(false);
      return;
    }
    
    // First run local validation
    if (!validateNationalId(nationalId)) {
      // Validation failed - refocus on the field
      setNationalIdValid(false);
      if (nationalIdInputRef.current) {
        nationalIdInputRef.current.focus();
      }
      return;
    }

    // Call API to check if National ID already has a registered file
    // This applies to all residency statuses: Saudi citizens, residency holders, and international applicants
    setNationalIdValidating(true);
    
    try {
      const country = site.isUAE ? 'AE' : 'SA';
      const response = await fetch(`/api/odoo/validate-national-id?nationalId=${encodeURIComponent(nationalId)}&country=${encodeURIComponent(country)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // HTTP error
        setModalMessage({
          en: 'There is a technical issue. Please try again later.',
          ar: 'يوجد مشكلة تقنية ، الرجاء المحاولة في وقت لاحق'
        });
        setShowModal(true);
        setNationalIdValid(false);
        if (nationalIdInputRef.current) {
          nationalIdInputRef.current.focus();
        }
        return;
      }

      const data = await response.json();
      console.log('API Response for National ID validation:', data);

      // Check for technical error from server
      if (data.technicalError) {
        setModalMessage({
          en: 'There is a technical issue. Please try again later.',
          ar: 'يوجد مشكلة تقنية ، الرجاء المحاولة في وقت لاحق'
        });
        setShowModal(true);
        setNationalIdValid(false);
        if (nationalIdInputRef.current) {
          nationalIdInputRef.current.focus();
        }
        return;
      }

      // Check the result data - it's nested in result.data for JSON-RPC responses
      const resultData = data?.result?.data || data?.data;
      console.log('Result data:', resultData);
      
      if (resultData === 'active_student_found') {
        // National ID already has an active registration
        setModalMessage({
          en: 'This National ID already has a registered file.',
          ar: 'رقم الهوية المدخل لديه ملف مسجل مسبقًا'
        });
        setShowModal(true);
        setNationalId(''); // Clear the field
        setNationalIdValid(false);
        if (nationalIdInputRef.current) {
          nationalIdInputRef.current.focus();
        }
      } else if (resultData === 'application_found') {
        // National ID already has an application
        setModalMessage({
          en: 'This National ID already has an application. Please contact support.',
          ar: 'رقم الهوية المدخل لديه طلب مسجل بالفعل. يرجى التواصل مع الدعم.'
        });
        setShowModal(true);
        setNationalId(''); // Clear the field
        setNationalIdValid(false);
        if (nationalIdInputRef.current) {
          nationalIdInputRef.current.focus();
        }
      } else if (resultData === 'draft_application_found') {
        // Draft application exists - allow them to continue (they can resume)
        setNationalIdValid(true);
      } else if (resultData === 'application_not_found') {
        // No application found - OK to proceed
        setNationalIdValid(true);
      } else {
        // Unknown response - show error
        setModalMessage({
          en: 'Something went wrong while validating your National ID. Please try again.',
          ar: 'حدث خطأ أثناء التحقق من رقم هويتك. يرجى المحاولة مرة أخرى.'
        });
        setShowModal(true);
        setNationalIdValid(false);
        if (nationalIdInputRef.current) {
          nationalIdInputRef.current.focus();
        }
      }
    } catch (error) {
      console.error('Error validating National ID:', error);
      setModalMessage({
        en: 'There is a technical issue. Please try again later.',
        ar: 'يوجد مشكلة تقنية ، الرجاء المحاولة في وقت لاحق'
      });
      setShowModal(true);
      setNationalIdValid(false);
      if (nationalIdInputRef.current) {
        nationalIdInputRef.current.focus();
      }
    } finally {
      setNationalIdValidating(false);
    }
  };

  const getPlaceholders = () => {
    const placeholders = {
      saudi_citizen: {
        nationalId: { en: 'National ID *', ar: 'رقم الهوية *' },
        idExpiry: { en: 'ID expiry date *', ar: 'تاريخ انتهاء الهوية *' }
      },
      emirati_citizen: {
        nationalId: { en: 'Emirates ID *', ar: 'رقم الهوية الإماراتية *' },
        idExpiry: { en: 'ID expiry date *', ar: 'تاريخ انتهاء الهوية *' }
      },
      residency_holder: {
        nationalId: { en: 'Iqama ID *', ar: 'رقم الاقامة *' },
        idExpiry: { en: 'Iqama expiry date *', ar: 'تاريخ انتهاء الاقامة *' }
      },
      international_applicant: {
        nationalId: { en: 'Passport ID *', ar: 'رقم الجواز *' },
        idExpiry: { en: 'Passport expiry date *', ar: 'تاريخ انتهاء جواز السفر *' }
      }
    };
    
    return placeholders[residencyStatus] || placeholders.saudi_citizen || placeholders.emirati_citizen;
  };

  // Age validation function
  const validateAge = (birthDate) => {
    if (!birthDate) return true;
    
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    
    if (age <= 15) {
      setModalMessage({
        en: 'The age should be more than 15 years',
        ar: 'العمر يجب ان يكون اكبر من 15 عاما'
      });
      setShowModal(true);
      return false;
    }
    return true;
  };

  const handleDobBlur = () => {
    if (dob) {
      validateAge(dob);
    }
  };

  // Handle English-only input
  const handleEnglishOnlyChange = (e, setter) => {
    const value = e.target.value;
    // Allow only English letters and spaces
    const englishOnly = value.replace(/[^a-zA-Z\s]/g, '');
    setter(englishOnly);
  };

  // Email validation (format only)
  const validateEmail = (email) => {
    // Stricter regex: no leading/trailing dots, no consecutive dots
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;
    if (!email) {
      setEmailError('');
      return false;
    }
    if (!emailRegex.test(email)) {
      setEmailError(isRTL ? 'بريد إلكتروني غير صحيح' : 'Invalid email format');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Mobile validation
  const handleMobileValidation = () => {
    if (!mobile) {
      setMobileError('');
      return;
    }
    
    if (mobileItiRef.current) {
      const isValid = mobileItiRef.current.isValidNumber();
      if (!isValid) {
        setMobileError(isRTL ? 'يرجى إدخال رقم هاتف صحيح' : 'Enter a valid phone number');
        return;
      }
    }
    
    setMobileError('');
  };

  // Phone validation
  const handlePhoneValidation = () => {
    if (!phone) {
      setPhoneError('');
      return;
    }
    
    if (phoneItiRef.current && mobileItiRef.current) {
      const phoneNumber = phoneItiRef.current.getNumber();
      const mobileNumber = mobileItiRef.current.getNumber();
      
      // Check if phone equals mobile
      if (phoneNumber === mobileNumber && phoneNumber) {
        setPhoneError(isRTL ? 'يرجى إدخال رقم هاتف آخر' : 'Enter different phone number');
        return;
      }
      
      // Validate phone number format
      const isValid = phoneItiRef.current.isValidNumber();
      if (!isValid) {
        setPhoneError(isRTL ? 'يرجى إدخال رقم هاتف صحيح' : 'Enter a valid phone number');
        return;
      }
    }
    
    setPhoneError('');
  };

  const nextButtonLabel = isRTL ? 'التالي' : 'Next';

  const wizardStyle = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: '20px',
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left',
  };

  const stepperStyle = {
    marginBottom: '30px',
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    direction: isRTL ? 'rtl' : 'ltr',
    width: '100%',
    boxSizing: 'border-box',
  };

  const contentStyle = {
    minHeight: '300px',
    padding: '30px',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '5px',
    marginBottom: '20px',
    direction: isRTL ? 'rtl' : 'ltr',
    textAlign: isRTL ? 'right' : 'left',
  };

  const buttonContainerStyle = {
    textAlign: isRTL ? 'left' : 'right',
    display: 'flex',
    justifyContent: isRTL ? 'flex-start' : 'flex-end',
  };

  const nextButtonStyle = {
    padding: '12px 40px',
    backgroundColor: isSubmitting ? '#6c757d' : '#26247b',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: (isSubmitting || currentStep >= steps.length) ? 'not-allowed' : 'pointer',
    opacity: (isSubmitting || currentStep >= steps.length) ? 0.6 : 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    flexDirection: isRTL ? 'row-reverse' : 'row',
  };

  // Field error style helper
  const getFieldErrorStyle = (fieldName) => fieldErrors[fieldName] ? { borderColor: '#dc3545' } : {};

  // Helper function for label styles with custom color
  const getLabelStyle = (additionalStyles = {}) => ({
    fontWeight: 'bold',
    marginBottom: '8px',
    display: 'block',
    color: '#26247b',
    ...additionalStyles
  });

  // Custom underline TextField component
  const UnderlineTextField = withStyles({
    root: {
      '& .MuiInput-input': {
        backgroundColor: '#fff',
        padding: '12px 15px',
        fontSize: '16px'
      },
      '& .MuiInput-underline:before': {
        borderBottomColor: '#ccc',
        borderBottomWidth: '1px'
      },
      '& .MuiInput-underline:hover:before': {
        borderBottomColor: '#ccc'
      },
      '& .MuiInput-underline.Mui-focused:after': {
        borderBottomColor: '#26247b',
        borderBottomWidth: '2px'
      }
    }
  })(TextField);

  // Create a CSS style block for underline input
  const underlineInputStyle = {
    backgroundColor: '#fff',
    border: 'none',
    borderBottom: '1px solid #ccc',
    padding: '12px 15px',
    fontSize: '16px',
    width: '100%',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.3s'
  };

  // Helper function for input focus/blur handlers
  const handleInputFocus = (e) => {
    e.target.style.borderBottom = '2px solid #26247b';
  };

  const handleInputBlur = (e) => {
    e.target.style.borderBottom = '1px solid #ccc';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h2 style={{ marginBottom: '30px', color: '#26247b' }}>{steps[0].label}</h2>
            
            {/* Level Dropdown */}
            <div className="form-group" style={{ marginBottom: '25px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              <label style={getLabelStyle({ textAlign: isRTL ? 'right' : 'left' })}>
                {isRTL ? 'اختر الدرجة' : 'Choose Level'} <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                value={level}
                onChange={(option) => {
                setLevel(option);
                setFieldErrors(prev => ({...prev, level: ''}));
                trackFieldChange('Choose Level', option?.label, 1);
                if (option?.value === 'sci_fnd') {
                  setModalType('warning');
                  setModalMessage({
                    en: "You have selected the Non-Science Track. This option is only for applicants who completed a Non-Scientific High School curriculum. If you hold a Scientific High School Certificate, you should apply through the Bachelor's admission track. Selecting the incorrect track may delay the processing of your application. Please confirm that you wish to continue",
                    ar: 'لقد اخترت المسار غير العلمي. هذا الخيار مخصص فقط للمتقدمين الذين أكملوا منهجاً دراسياً ثانوياً غير علمي. إذا كنت تحمل شهادة ثانوية علمية، فيجب عليك التقديم من خلال مسار القبول الخاص بالبكالوريوس. اختيار المسار غير الصحيح قد يؤدي إلى تأخير معالجة طلبك. يرجى التأكيد على رغبتك في المتابعة'
                  });
                  setShowModal(true);
                }
              }}
                placeholder={isRTL ? 'الرجاء الاختيار *' : 'Please select *'}
                options={getLevelOptions()}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '44px',
                    borderRadius: '4px',
                    borderColor: state.isFocused ? '#26247b' : (fieldErrors.level ? '#dc3545' : '#ced4da'),
                    boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                    '&:hover': {
                      borderColor: state.isFocused ? '#26247b' : '#ced4da'
                    }
                  })
                }}
              />
              {fieldErrors.level && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.level}</small>}
            </div>

            {/* Campus Dropdown */}
            <div className="form-group" style={{ marginBottom: '25px' }}>
              <label style={getLabelStyle()}>
                {isRTL ? 'اختر الحرم الجامعي' : 'Choose Campus'} <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                value={campus}
                onChange={(option) => { 
                  setCampus(option); 
                  setFieldErrors(prev => ({...prev, campus: ''}));
                  trackFieldChange('Choose Campus', option?.label, 1);
                }}
                placeholder={intakeLoading 
                  ? (isRTL ? 'جاري التحميل...' : 'Loading...') 
                  : (isRTL ? 'الرجاء الاختيار *' : 'Please select *')
                }
                options={campusOptions}
                isDisabled={!level || intakeLoading || campusOptions.length === 0}
                isLoading={intakeLoading}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '44px',
                    borderRadius: '4px',
                    borderColor: state.isFocused ? '#26247b' : (fieldErrors.campus ? '#dc3545' : '#ced4da'),
                    boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                    backgroundColor: (!level || intakeLoading || campusOptions.length === 0) ? '#e9ecef' : 'white',
                    '&:hover': {
                      borderColor: state.isFocused ? '#26247b' : '#ced4da'
                    }
                  })
                }}
              />
              {fieldErrors.campus && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.campus}</small>}
              <small style={{ color: '#6c757d', display: 'block', marginTop: '5px' }}>
                {!level 
                  ? (isRTL ? 'سيتم تحميلها بعد اختيار الدرجة' : 'Will be loaded after selecting level')
                  : campusOptions.length === 0 && !intakeLoading
                  ? (isRTL ? 'لا توجد خيارات متاحة' : 'No options available')
                  : ''
                }
              </small>
            </div>

            {/* Intake Dropdown */}
            <div className="form-group" style={{ marginBottom: '25px' }}>
              <label style={getLabelStyle()}>
                {isRTL ? 'اختر المسار' : 'Choose Intake'} <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                value={intake}
                onChange={(option) => { 
                  setIntake(option); 
                  setFieldErrors(prev => ({...prev, intake: ''}));
                  trackFieldChange('Choose Intake', option?.label, 1);
                  // Populate major options from selected intake's programs
                  if (option?.programs && Array.isArray(option.programs)) {
                    const majors = option.programs.map(prog => ({
                      value: prog.code,
                      label: prog.name
                    }));
                    setMajorOptions(majors);
                    setPyMajor(null); // Reset major selection when intake changes
                  } else {
                    setMajorOptions([]);
                    setPyMajor(null);
                  }
                }}
                placeholder={isRTL ? 'الرجاء الاختيار *' : 'Please select *'}
                options={intakeOptions}
                isDisabled={!campus || intakeOptions.length === 0}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '44px',
                    borderRadius: '4px',
                    borderColor: state.isFocused ? '#26247b' : (fieldErrors.intake ? '#dc3545' : '#ced4da'),
                    boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                    backgroundColor: (!campus || intakeOptions.length === 0) ? '#e9ecef' : 'white',
                    '&:hover': {
                      borderColor: state.isFocused ? '#26247b' : '#ced4da'
                    }
                  })
                }}
              />
              {fieldErrors.intake && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.intake}</small>}
              <small style={{ color: '#6c757d', display: 'block', marginTop: '5px' }}>
                {!campus 
                  ? (isRTL ? 'سيتم تحميلها بعد اختيار الحرم الجامعي' : 'Will be loaded after selecting campus')
                  : intakeOptions.length === 0
                  ? (isRTL ? 'لا توجد خيارات متاحة' : 'No options available')
                  : ''
                }
              </small>
            </div>

            {/* Residency Status */}
            <div className="form-group" style={{ marginBottom: '25px' }}>
              <label style={getLabelStyle()}>
                {isRTL ? 'معلومات الاقامة' : 'Residency Status'} <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                value={{ 
                  value: residencyStatus, 
                  label: (residencyStatus === 'saudi_citizen' || residencyStatus === 'emirati_citizen')
                    ? (isRTL ? 'مواطن' : 'Citizen')
                    : residencyStatus === 'residency_holder'
                    ? (isRTL ? 'مقيم' : 'Residency Holder')
                    : (isRTL ? 'متقدم دولي' : 'International Applicant')
                }}
                onChange={(option) => {
                  setResidencyStatus(option.value);
                  trackFieldChange('Residency Status', option?.label, 1);
                  // Clear National ID and its validation state when switching residency status
                  setNationalId('');
                  setNationalIdValid(false);
                  setFieldErrors(prev => ({...prev, nationalId: ''}));
                  // Clear nationality when switching to citizen (Saudi or Emirati)
                  if (option.value === 'saudi_citizen' || option.value === 'emirati_citizen') {
                    setNationality(null);
                  }
                }}
                options={[
                  { value: site.isUAE ? 'emirati_citizen' : 'saudi_citizen', label: isRTL ? 'مواطن' : 'Citizen' },
                  { value: 'residency_holder', label: isRTL ? 'مقيم' : 'Residency Holder' },
                  { value: 'international_applicant', label: isRTL ? 'متقدم دولي' : 'International Applicant' }
                ]}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '44px',
                    borderRadius: '4px',
                    borderColor: state.isFocused ? '#26247b' : '#ced4da',
                    boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                    '&:hover': {
                      borderColor: state.isFocused ? '#26247b' : '#ced4da'
                    }
                  })
                }}
              />
            </div>

            {/* Civil Registry Section */}
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ marginBottom: '20px', color: '#26247b', fontWeight: 'bold' }}>
                {isRTL ? 'السجل المدني' : 'Civil Registry'}
              </h4>

              {/* National ID / Iqama / Passport */}
              <div className="form-group" style={{ marginBottom: '20px', position: 'relative' }}>
                <input
                  ref={nationalIdInputRef}
                  type="text"
                  placeholder={isRTL ? getPlaceholders().nationalId.ar : getPlaceholders().nationalId.en}
                  value={nationalId}
                  onChange={(e) => { handleNationalIdChange(e); setFieldErrors(prev => ({...prev, nationalId: ''})); setNationalIdValid(false); }}
                  onKeyDown={handleNationalIdKeyDown}
                  onBlur={(e) => { handleInputBlur(e); handleNationalIdBlur(); }}
                  onFocus={handleInputFocus}
                  disabled={nationalIdValidating}
                  style={{
                    ...underlineInputStyle,
                    backgroundColor: nationalIdValidating ? '#f8f9fa' : '#fff'
                  }}
                />
                {nationalIdValidating && (
                  <span style={{
                    position: 'absolute',
                    [isRTL ? 'left' : 'right']: '15px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '14px',
                    color: '#6c757d'
                  }}>
                    {isRTL ? 'جاري التحقق...' : 'Validating...'}
                  </span>
                )}
                {fieldErrors.nationalId && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.nationalId}</small>}
              </div>

              {/* Nationality - Only show when not a Citizen (Saudi or Emirati) */}
              {residencyStatus !== 'saudi_citizen' && residencyStatus !== 'emirati_citizen' && (
              <div className="form-group" style={{ marginBottom: '0' }}>
                <Select
                  placeholder={isRTL ? 'الجنسية *' : 'Nationality *'}
                options={[
                  { value: 'AF', label: isRTL ? 'أفغانستان' : 'Afghanistan' },
                  { value: 'AL', label: isRTL ? 'ألبانيا' : 'Albania' },
                  { value: 'DZ', label: isRTL ? 'الجزائر' : 'Algeria' },
                  { value: 'AS', label: isRTL ? 'ساموا الأمريكية' : 'American Samoa' },
                  { value: 'AD', label: isRTL ? 'أندورا' : 'Andorra' },
                  { value: 'AO', label: isRTL ? 'أنجولا' : 'Angola' },
                  { value: 'AI', label: isRTL ? 'أنجويلا' : 'Anguilla' },
                  { value: 'AQ', label: isRTL ? 'انتاركتيكا' : 'Antarctica' },
                  { value: 'AG', label: isRTL ? 'أنتيغا وباربودا' : 'Antigua and Barbuda' },
                  { value: 'AR', label: isRTL ? 'الأرجنتين' : 'Argentina' },
                  { value: 'AM', label: isRTL ? 'أرمينيا' : 'Armenia' },
                  { value: 'AW', label: isRTL ? 'جزيرة أروبا' : 'Aruba' },
                  { value: 'AU', label: isRTL ? 'استراليا' : 'Australia' },
                  { value: 'AT', label: isRTL ? 'النمسا' : 'Austria' },
                  { value: 'AZ', label: isRTL ? 'أذربيجان' : 'Azerbaijan' },
                  { value: 'BS', label: isRTL ? 'جزر الباهاما' : 'Bahamas' },
                  { value: 'BH', label: isRTL ? 'مملكة البحرين' : 'Bahrain' },
                  { value: 'BD', label: isRTL ? 'بنغلاديش' : 'Bangladesh' },
                  { value: 'BB', label: isRTL ? 'باربادوس' : 'Barbados' },
                  { value: 'X5', label: isRTL ? 'بورماوي' : 'Barmawi' },
                  { value: 'BY', label: isRTL ? 'بيلاروس' : 'Belarus' },
                  { value: 'BE', label: isRTL ? 'بلجيكا' : 'Belgium' },
                  { value: 'BZ', label: isRTL ? 'بليز' : 'Belize' },
                  { value: 'BJ', label: isRTL ? 'بنين' : 'Benin' },
                  { value: 'BM', label: isRTL ? 'برمودا' : 'Bermuda' },
                  { value: 'BT', label: isRTL ? 'بوتان' : 'Bhutan' },
                  { value: 'X1', label: isRTL ? 'بلوشي' : 'Blushi' },
                  { value: 'BO', label: isRTL ? 'بوليفيا' : 'Bolivia' },
                  { value: 'BQ', label: isRTL ? 'بونير، سينت أوستاتيوس، وصبا' : 'Bonaire, Sint Eustatius and Saba' },
                  { value: 'BA', label: isRTL ? 'البوسنة والهرسك' : 'Bosnia and Herzegovina' },
                  { value: 'BW', label: isRTL ? 'بوتسوانا' : 'Botswana' },
                  { value: 'BV', label: isRTL ? 'جزيرة بوفي' : 'Bouvet Island' },
                  { value: 'BR', label: isRTL ? 'البرازيل' : 'Brazil' },
                  { value: 'IO', label: isRTL ? 'مقاطعة المحيط الهندي البريطانية' : 'British Indian Ocean Territory' },
                  { value: 'BN', label: isRTL ? 'بروناي دار السلام' : 'Brunei Darussalam' },
                  { value: 'BG', label: isRTL ? 'بلغاريا' : 'Bulgaria' },
                  { value: 'BF', label: isRTL ? 'بوركينا فاسو' : 'Burkina Faso' },
                  { value: 'BI', label: isRTL ? 'بوروندي' : 'Burundi' },
                  { value: 'KH', label: isRTL ? 'كمبوديا' : 'Cambodia' },
                  { value: 'CM', label: isRTL ? 'الكاميرون' : 'Cameroon' },
                  { value: 'CA', label: isRTL ? 'كندا' : 'Canada' },
                  { value: 'CV', label: isRTL ? 'الرأس الأخضر' : 'Cape Verde' },
                  { value: 'KY', label: isRTL ? 'جزر الكايمان' : 'Cayman Islands' },
                  { value: 'CF', label: isRTL ? 'جمهورية أفريقيا الوسطى' : 'Central African Republic' },
                  { value: 'TD', label: isRTL ? 'تشاد' : 'Chad' },
                  { value: 'CL', label: isRTL ? 'تشيلي' : 'Chile' },
                  { value: 'CN', label: isRTL ? 'الصين' : 'China' },
                  { value: 'CX', label: isRTL ? 'جزيرة الكريسماس' : 'Christmas Island' },
                  { value: 'CC', label: isRTL ? 'جزر الكوكوس' : 'Cocos (Keeling) Islands' },
                  { value: 'CO', label: isRTL ? 'كولومبيا' : 'Colombia' },
                  { value: 'KM', label: isRTL ? 'جزر القمر' : 'Comoros' },
                  { value: 'CG', label: isRTL ? 'الكونغو' : 'Congo' },
                  { value: 'CK', label: isRTL ? 'جزر كوك' : 'Cook Islands' },
                  { value: 'CR', label: isRTL ? 'كوستاريكا' : 'Costa Rica' },
                  { value: 'HR', label: isRTL ? 'كرواتيا' : 'Croatia' },
                  { value: 'CU', label: isRTL ? 'كوبا' : 'Cuba' },
                  { value: 'CW', label: isRTL ? 'كوراساو' : 'Curaçao' },
                  { value: 'CY', label: isRTL ? 'قبرص' : 'Cyprus' },
                  { value: 'CZ', label: isRTL ? 'جمهورية التشيك' : 'Czech Republic' },
                  { value: 'CI', label: isRTL ? 'كوت ديفوار' : 'Côte d\'Ivoire' },
                  { value: 'CD', label: isRTL ? 'جمهورية الكونغو الديمقراطية' : 'Democratic Republic of the Congo' },
                  { value: 'DK', label: isRTL ? 'الدنمارك' : 'Denmark' },
                  { value: 'X6', label: isRTL ? 'القبائل النازحة' : 'Displaced Tribes' },
                  { value: 'DJ', label: isRTL ? 'جيبوتي' : 'Djibouti' },
                  { value: 'DM', label: isRTL ? 'دومينيكا' : 'Dominica' },
                  { value: 'DO', label: isRTL ? 'جمهورية الدومينيكان' : 'Dominican Republic' },
                  { value: 'EC', label: isRTL ? 'الإكوادور' : 'Ecuador' },
                  { value: 'EG', label: isRTL ? 'مصر' : 'Egypt' },
                  { value: 'SV', label: isRTL ? 'السلفادور' : 'El Salvador' },
                  { value: 'GQ', label: isRTL ? 'غينيا الاستوائية' : 'Equatorial Guinea' },
                  { value: 'ER', label: isRTL ? 'إريتريا' : 'Eritrea' },
                  { value: 'EE', label: isRTL ? 'استونيا' : 'Estonia' },
                  { value: 'ET', label: isRTL ? 'إثيوبيا' : 'Ethiopia' },
                  { value: 'FK', label: isRTL ? 'جزر الفالكلاند' : 'Falkland Islands' },
                  { value: 'FO', label: isRTL ? 'جزر فارو' : 'Faroe Islands' },
                  { value: 'FJ', label: isRTL ? 'فيجي' : 'Fiji' },
                  { value: 'FI', label: isRTL ? 'فنلندا' : 'Finland' },
                  { value: 'FR', label: isRTL ? 'فرنسا' : 'France' },
                  { value: 'GF', label: isRTL ? 'غويانا الفرنسية' : 'French Guiana' },
                  { value: 'PF', label: isRTL ? 'بولينيزيا الفرنسية' : 'French Polynesia' },
                  { value: 'TF', label: isRTL ? 'الأقاليم الشمالية الفرنسية' : 'French Southern Territories' },
                  { value: 'GA', label: isRTL ? 'الجابون' : 'Gabon' },
                  { value: 'GM', label: isRTL ? 'غامبيا' : 'Gambia' },
                  { value: 'GE', label: isRTL ? 'جورجيا' : 'Georgia' },
                  { value: 'DE', label: isRTL ? 'ألمانيا' : 'Germany' },
                  { value: 'GH', label: isRTL ? 'غانا' : 'Ghana' },
                  { value: 'GI', label: isRTL ? 'جبل طارق' : 'Gibraltar' },
                  { value: 'GR', label: isRTL ? 'اليونان' : 'Greece' },
                  { value: 'GL', label: isRTL ? 'جرين ﻻند' : 'Greenland' },
                  { value: 'GD', label: isRTL ? 'غرناطة' : 'Grenada' },
                  { value: 'GP', label: isRTL ? 'غوادلوب' : 'Guadeloupe' },
                  { value: 'GU', label: isRTL ? 'غوام' : 'Guam' },
                  { value: 'GT', label: isRTL ? 'غواتيمالا' : 'Guatemala' },
                  { value: 'GG', label: isRTL ? 'غيرنسي' : 'Guernsey' },
                  { value: 'GN', label: isRTL ? 'غينيا' : 'Guinea' },
                  { value: 'GW', label: isRTL ? 'غينيا بيساو' : 'Guinea-Bissau' },
                  { value: 'GY', label: isRTL ? 'غيانا' : 'Guyana' },
                  { value: 'HT', label: isRTL ? 'هاييتي' : 'Haiti' },
                  { value: 'HM', label: isRTL ? 'جزيرة هيرد وجزر ماكدونالد' : 'Heard Island and McDonald Islands' },
                  { value: 'VA', label: isRTL ? 'الكرسي الرسولي (ولاية مدينة الفاتيكان)' : 'Holy See (Vatican City State)' },
                  { value: 'HN', label: isRTL ? 'هندوراس' : 'Honduras' },
                  { value: 'HK', label: isRTL ? 'هونغ كونغ' : 'Hong Kong' },
                  { value: 'HU', label: isRTL ? 'المجر' : 'Hungary' },
                  { value: 'IS', label: isRTL ? 'آيسلندا' : 'Iceland' },
                  { value: 'IN', label: isRTL ? 'الهند' : 'India' },
                  { value: 'ID', label: isRTL ? 'إندونيسيا' : 'Indonesia' },
                  { value: 'IR', label: isRTL ? 'إيران' : 'Iran' },
                  { value: 'IQ', label: isRTL ? 'العراق' : 'Iraq' },
                  { value: 'IE', label: isRTL ? 'إيرلندا' : 'Ireland' },
                  { value: 'IM', label: isRTL ? 'جزيرة آيل أوف مان' : 'Isle of Man' },
                  { value: 'IL', label: isRTL ? 'إسرائيل' : 'Israel' },
                  { value: 'IT', label: isRTL ? 'ايطاليا' : 'Italy' },
                  { value: 'JM', label: isRTL ? 'جامايكا' : 'Jamaica' },
                  { value: 'JP', label: isRTL ? 'اليابان' : 'Japan' },
                  { value: 'JE', label: isRTL ? 'جيرسي' : 'Jersey' },
                  { value: 'JO', label: isRTL ? 'الأردن' : 'Jordan' },
                  { value: 'KZ', label: isRTL ? 'كازاخستان' : 'Kazakhstan' },
                  { value: 'KE', label: isRTL ? 'كينيا' : 'Kenya' },
                  { value: 'KI', label: isRTL ? 'كيريباتي' : 'Kiribati' },
                  { value: 'XK', label: isRTL ? 'كوسوفو' : 'Kosovo' },
                  { value: 'KW', label: isRTL ? 'الكويت' : 'Kuwait' },
                  { value: 'KG', label: isRTL ? 'قرغيزستان' : 'Kyrgyzstan' },
                  { value: 'LA', label: isRTL ? 'لاوس' : 'Laos' },
                  { value: 'LV', label: isRTL ? 'لاتفيا' : 'Latvia' },
                  { value: 'LB', label: isRTL ? 'لبنان' : 'Lebanon' },
                  { value: 'LS', label: isRTL ? 'ليسوتو' : 'Lesotho' },
                  { value: 'LR', label: isRTL ? 'ليبيريا' : 'Liberia' },
                  { value: 'LY', label: isRTL ? 'ليبيا' : 'Libya' },
                  { value: 'LI', label: isRTL ? 'ليشتنشتاين' : 'Liechtenstein' },
                  { value: 'LT', label: isRTL ? 'ليثوانيا' : 'Lithuania' },
                  { value: 'LU', label: isRTL ? 'لوكسمبورغ' : 'Luxembourg' },
                  { value: 'MO', label: isRTL ? 'ماكاو' : 'Macau' },
                  { value: 'MG', label: isRTL ? 'مدغشقر' : 'Madagascar' },
                  { value: 'MW', label: isRTL ? 'مالاوي' : 'Malawi' },
                  { value: 'MY', label: isRTL ? 'ماليزيا' : 'Malaysia' },
                  { value: 'MV', label: isRTL ? 'جزر المالديف' : 'Maldives' },
                  { value: 'ML', label: isRTL ? 'مالي' : 'Mali' },
                  { value: 'MT', label: isRTL ? 'مالطة' : 'Malta' },
                  { value: 'MH', label: isRTL ? 'جزر المارشال' : 'Marshall Islands' },
                  { value: 'MQ', label: isRTL ? 'مارتينيك' : 'Martinique' },
                  { value: 'MR', label: isRTL ? 'موريتانيا' : 'Mauritania' },
                  { value: 'MU', label: isRTL ? 'موريشيوس' : 'Mauritius' },
                  { value: 'YT', label: isRTL ? 'مايوت' : 'Mayotte' },
                  { value: 'MX', label: isRTL ? 'المكسيك' : 'Mexico' },
                  { value: 'FM', label: isRTL ? 'مكرونيسيا' : 'Micronesia' },
                  { value: 'MD', label: isRTL ? 'مولدوفا' : 'Moldova' },
                  { value: 'MC', label: isRTL ? 'موناكو' : 'Monaco' },
                  { value: 'MN', label: isRTL ? 'منغوليا' : 'Mongolia' },
                  { value: 'ME', label: isRTL ? 'الجبل الأسود' : 'Montenegro' },
                  { value: 'MS', label: isRTL ? 'مونتسيرات' : 'Montserrat' },
                  { value: 'MA', label: isRTL ? 'المغرب' : 'Morocco' },
                  { value: 'MZ', label: isRTL ? 'موزامبيق' : 'Mozambique' },
                  { value: 'MM', label: isRTL ? 'ميانمار' : 'Myanmar' },
                  { value: 'NA', label: isRTL ? 'ناميبيا' : 'Namibia' },
                  { value: 'NR', label: isRTL ? 'ناورو' : 'Nauru' },
                  { value: 'NP', label: isRTL ? 'نيبال' : 'Nepal' },
                  { value: 'NL', label: isRTL ? 'هولندا' : 'Netherlands' },
                  { value: 'NC', label: isRTL ? 'كاليدونيا الجديدة' : 'New Caledonia' },
                  { value: 'NZ', label: isRTL ? 'نيوزيلندا' : 'New Zealand' },
                  { value: 'NI', label: isRTL ? 'نيكاراجوا' : 'Nicaragua' },
                  { value: 'NE', label: isRTL ? 'النيجر' : 'Niger' },
                  { value: 'NG', label: isRTL ? 'نيجيريا' : 'Nigeria' },
                  { value: 'NU', label: isRTL ? 'نييوي' : 'Niue' },
                  { value: 'NF', label: isRTL ? 'جزيرة نورفولك' : 'Norfolk Island' },
                  { value: 'KP', label: isRTL ? 'كوريا الشمالية' : 'North Korea' },
                  { value: 'MK', label: isRTL ? 'شمال مقدونيا' : 'North Macedonia' },
                  { value: 'MP', label: isRTL ? 'جزر ماريانا الشمالية' : 'Northern Mariana Islands' },
                  { value: 'NO', label: isRTL ? 'النرويج' : 'Norway' },
                  { value: 'OM', label: isRTL ? 'سلطنة عمان' : 'Oman' },
                  { value: 'X2', label: isRTL ? 'مقيم اخر' : 'Others-Muqeem' },
                  { value: 'PK', label: isRTL ? 'باكستان' : 'Pakistan' },
                  { value: 'PW', label: isRTL ? 'بالاو' : 'Palau' },
                  { value: 'PA', label: isRTL ? 'بنما' : 'Panama' },
                  { value: 'PG', label: isRTL ? 'بابوا غينيا الجديدة' : 'Papua New Guinea' },
                  { value: 'PY', label: isRTL ? 'باراجواي' : 'Paraguay' },
                  { value: 'PE', label: isRTL ? 'بيرو' : 'Peru' },
                  { value: 'PH', label: isRTL ? 'الفلبين' : 'Philippines' },
                  { value: 'PN', label: isRTL ? 'جزر بيتكيرن' : 'Pitcairn Islands' },
                  { value: 'PL', label: isRTL ? 'بولندا' : 'Poland' },
                  { value: 'PT', label: isRTL ? 'البرتغال' : 'Portugal' },
                  { value: 'PR', label: isRTL ? 'بورتوريكو' : 'Puerto Rico' },
                  { value: 'QA', label: isRTL ? 'قطر' : 'Qatar' },
                  { value: 'X3', label: isRTL ? 'غير محدد' : 'Resident' },
                  { value: 'RO', label: isRTL ? 'رومانيا' : 'Romania' },
                  { value: 'RU', label: isRTL ? 'الاتحاد الروسي' : 'Russian Federation' },
                  { value: 'RW', label: isRTL ? 'رواندا' : 'Rwanda' },
                  { value: 'RE', label: isRTL ? 'ريونيون' : 'Réunion' },
                  { value: 'BL', label: isRTL ? 'سانت بارتيليمي' : 'Saint Barthélémy' },
                  { value: 'SH', label: isRTL ? 'سانت هيلينا، أسنسيون، وتريستان دا كونا' : 'Saint Helena, Ascension and Tristan da Cunha' },
                  { value: 'KN', label: isRTL ? 'سانت كيتس ونيفيس' : 'Saint Kitts and Nevis' },
                  { value: 'LF', label: isRTL ? 'سانت مارتن (الجزء الفرنسي)' : 'Saint Martin (French part)' },
                  { value: 'PM', label: isRTL ? 'سان بيار وميكلون' : 'Saint Pierre and Miquelon' },
                  { value: 'VC', label: isRTL ? 'سانت فينسنت وجزر غرينادين' : 'Saint Vincent and the Grenadines' },
                  { value: 'WS', label: isRTL ? 'ساموا' : 'Samoa' },
                  { value: 'SM', label: isRTL ? 'سان مارينو' : 'San Marino' },
                  { value: 'SN', label: isRTL ? 'السنغال' : 'Senegal' },
                  { value: 'RS', label: isRTL ? 'صربيا' : 'Serbia' },
                  { value: 'SC', label: isRTL ? 'سيشيل' : 'Seychelles' },
                  { value: 'SL', label: isRTL ? 'سيراليون' : 'Sierra Leone' },
                  { value: 'SG', label: isRTL ? 'سنغافورة' : 'Singapore' },
                  { value: 'SX', label: isRTL ? 'سينت مارتن (الجزء الهولندي)' : 'Sint Maarten (Dutch part)' },
                  { value: 'SK', label: isRTL ? 'سلوفاكيا' : 'Slovakia' },
                  { value: 'SI', label: isRTL ? 'سلوفينيا' : 'Slovenia' },
                  { value: 'SB', label: isRTL ? 'جزر سليمان' : 'Solomon Islands' },
                  { value: 'SO', label: isRTL ? 'الصومال' : 'Somalia' },
                  { value: 'ZA', label: isRTL ? 'جنوب إفريقيا' : 'South Africa' },
                  { value: 'GS', label: isRTL ? 'جورجيا الجنوبية وجزر ساندويتش الجنوبية' : 'South Georgia and the South Sandwich Islands' },
                  { value: 'KR', label: isRTL ? 'كوريا الجنوبية' : 'South Korea' },
                  { value: 'SS', label: isRTL ? 'جنوب السودان' : 'South Sudan' },
                  { value: 'ES', label: isRTL ? 'أسبانيا' : 'Spain' },
                  { value: 'LK', label: isRTL ? 'سريلانكا' : 'Sri Lanka' },
                  { value: 'PS', label: isRTL ? 'الدولة الفلسطينية' : 'State of Palestine' },
                  { value: 'SD', label: isRTL ? 'السودان' : 'Sudan' },
                  { value: 'SR', label: isRTL ? 'اسم سوري' : 'Suriname' },
                  { value: 'SJ', label: isRTL ? 'سفالبارد ويان ماين' : 'Svalbard and Jan Mayen' },
                  { value: 'SZ', label: isRTL ? 'سوازيلاند' : 'Swaziland' },
                  { value: 'SE', label: isRTL ? 'السويد' : 'Sweden' },
                  { value: 'CH', label: isRTL ? 'سويسرا' : 'Switzerland' },
                  { value: 'SY', label: isRTL ? 'سوريا' : 'Syria' },
                  { value: 'ST', label: isRTL ? 'ساو تومي وبرينسيبي' : 'São Tomé and Príncipe' },
                  { value: 'TW', label: isRTL ? 'تايوان' : 'Taiwan' },
                  { value: 'TJ', label: isRTL ? 'طاجكستان' : 'Tajikistan' },
                  { value: 'TZ', label: isRTL ? 'تنزانيا' : 'Tanzania' },
                  { value: 'TH', label: isRTL ? 'تايلاند' : 'Thailand' },
                  { value: 'TL', label: isRTL ? 'تيمور الشرقية' : 'Timor-Leste' },
                  { value: 'TG', label: isRTL ? 'توغو' : 'Togo' },
                  { value: 'TK', label: isRTL ? 'توكلو' : 'Tokelau' },
                  { value: 'TO', label: isRTL ? 'تونجا' : 'Tonga' },
                  { value: 'TT', label: isRTL ? 'ترينيداد وتوباغو' : 'Trinidad and Tobago' },
                  { value: 'TN', label: isRTL ? 'تونس' : 'Tunisia' },
                  { value: 'TR', label: isRTL ? 'تركيا' : 'Turkey' },
                  { value: 'X4', label: isRTL ? 'مقيم تركستاني' : 'Turkistani Resident' },
                  { value: 'TM', label: isRTL ? 'توركمنستان' : 'Turkmenistan' },
                  { value: 'TC', label: isRTL ? 'جزر تركس وكايكوس' : 'Turks and Caicos Islands' },
                  { value: 'TV', label: isRTL ? 'توفالو' : 'Tuvalu' },
                  { value: 'UM', label: isRTL ? 'جزر الولايات المتحدة البعيدة الصغيرة' : 'USA Minor Outlying Islands' },
                  { value: 'UG', label: isRTL ? 'أوغندا' : 'Uganda' },
                  { value: 'UA', label: isRTL ? 'أوكرانيا' : 'Ukraine' },
                  { value: 'AE', label: isRTL ? 'الإمارات العربية المتحدة' : 'United Arab Emirates' },
                  { value: 'GB', label: isRTL ? 'المملكة المتحدة' : 'United Kingdom' },
                  { value: 'US', label: isRTL ? 'الولايات المتحدة' : 'United States' },
                  { value: 'UY', label: isRTL ? 'أوروغواي' : 'Uruguay' },
                  { value: 'UZ', label: isRTL ? 'أوزبكستان' : 'Uzbekistan' },
                  { value: 'VU', label: isRTL ? 'فانوتوا' : 'Vanuatu' },
                  { value: 'VE', label: isRTL ? 'فينزويلا' : 'Venezuela' },
                  { value: 'VN', label: isRTL ? 'فيتنام' : 'Vietnam' },
                  { value: 'VG', label: isRTL ? 'جزر فيرجين (البريطانية)' : 'Virgin Islands (British)' },
                  { value: 'VI', label: isRTL ? 'جزر فيرجين (الولايات المتحدة اﻻمريكية)' : 'Virgin Islands (USA)' },
                  { value: 'WF', label: isRTL ? 'واليس وفوتونا' : 'Wallis and Futuna' },
                  { value: 'EH', label: isRTL ? 'الصحراء الغربية' : 'Western Sahara' },
                  { value: 'YE', label: isRTL ? 'اليمن' : 'Yemen' },
                  { value: 'ZM', label: isRTL ? 'زامبيا' : 'Zambia' },
                  { value: 'ZW', label: isRTL ? 'زيمبابوي' : 'Zimbabwe' },
                  { value: 'AX', label: isRTL ? 'جزر آلاند' : 'Åland Islands' }
                ]}
                value={nationality}
                onChange={(option) => setNationality(option)}
                styles={{
                  control: (base, state) => ({
                    ...base,
                    minHeight: '44px',
                    borderRadius: '4px',
                    borderColor: state.isFocused ? '#26247b' : '#ced4da',
                    boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                    '&:hover': {
                      borderColor: state.isFocused ? '#26247b' : '#ced4da'
                    }
                  })
                }}
              />
              {fieldErrors.nationality && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.nationality}</small>}
              </div>
              )}
            </div>

            {/* Gender & Birthdate Section */}
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ marginBottom: '20px', color: '#26247b', fontWeight: 'bold' }}>
                {isRTL ? 'الجنس' : 'Gender'}
              </h4>

              {/* Gender Radio Buttons */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <div className="gender-options" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <label 
                    className="gender-option"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      border: gender === 'male' ? '2px solid #26247b' : '2px solid #ffffff',
                      backgroundColor: gender === 'male' ? '#26247b' : '#ffffff',
                      color: gender === 'male' ? '#ffffff' : '#333333',
                      minWidth: '140px',
                      transition: 'all 0.2s ease',
                      flex: '1 1 auto'
                    }}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value="male"
                      checked={gender === 'male'}
                      onChange={(e) => { setGender(e.target.value); setFieldErrors(prev => ({...prev, gender: ''})); }}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: gender === 'male' ? '2px solid #ffffff' : '2px solid #26247b',
                      marginRight: isRTL ? '0' : '10px',
                      marginLeft: isRTL ? '10px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {gender === 'male' && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff'
                        }}></span>
                      )}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{isRTL ? 'ذكر' : 'Male'}</span>
                  </label>
                  <label 
                    className="gender-option"
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      border: gender === 'female' ? '2px solid #26247b' : '2px solid #ffffff',
                      backgroundColor: gender === 'female' ? '#26247b' : '#ffffff',
                      color: gender === 'female' ? '#ffffff' : '#333333',
                      minWidth: '140px',
                      transition: 'all 0.2s ease',
                      flex: '1 1 auto'
                    }}
                  >
                    <input
                      type="radio"
                      name="gender"
                      value="female"
                      checked={gender === 'female'}
                      onChange={(e) => { setGender(e.target.value); setFieldErrors(prev => ({...prev, gender: ''})); }}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: gender === 'female' ? '2px solid #ffffff' : '2px solid #26247b',
                      marginRight: isRTL ? '0' : '10px',
                      marginLeft: isRTL ? '10px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {gender === 'female' && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff'
                        }}></span>
                      )}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{isRTL ? 'انثى' : 'Female'}</span>
                  </label>
                </div>
                {fieldErrors.gender && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.gender}</small>}
              </div>
            </div>

            {/* Full Name Section */}
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ marginBottom: '20px', color: '#26247b', fontWeight: 'bold' }}>
                {isRTL ? 'الاسم الكامل باللغة الانجليزية' : 'Full Name in English'}
              </h4>

              {/* First Name */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  autoComplete="new-password"
                  placeholder={isRTL ? 'الاسم الاول *' : 'First Name *'}
                  value={firstNameEn}
                  onChange={(e) => { handleEnglishOnlyChange(e, setFirstNameEn); setFieldErrors(prev => ({...prev, firstNameEn: ''})); }}
                  onFocus={(e) => {
                    e.target.style.borderBottom = '2px solid #26247b';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderBottom = '1px solid #ccc';
                  }}
                  style={underlineInputStyle}
                />
                {fieldErrors.firstNameEn && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.firstNameEn}</small>}
              </div>

              {/* Second Name (Father Name) */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  autoComplete="new-password"
                  placeholder={isRTL ? 'اسم الاب *' : 'Father Name *'}
                  value={secondNameEn}
                  onChange={(e) => { handleEnglishOnlyChange(e, setSecondNameEn); setFieldErrors(prev => ({...prev, secondNameEn: ''})); }}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  style={underlineInputStyle}
                />
                {fieldErrors.secondNameEn && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.secondNameEn}</small>}
              </div>

              {/* Third Name (Grand Father Name) */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <input
                  type="text"
                  autoComplete="new-password"
                  placeholder={isRTL ? 'اسم الجد' : 'Grand Father Name'}
                  value={thirdNameEn}
                  onChange={(e) => handleEnglishOnlyChange(e, setThirdNameEn)}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  style={underlineInputStyle}
                />
              </div>

              {/* Last Name (Family Father Name) */}
              <div className="form-group" style={{ marginBottom: '0' }}>
                <input
                  type="text"
                  autoComplete="new-password"
                  placeholder={isRTL ? 'اسم العائلة *' : 'Family Father Name *'}
                  value={lastNameEn}
                  onChange={(e) => { handleEnglishOnlyChange(e, setLastNameEn); setFieldErrors(prev => ({...prev, lastNameEn: ''})); }}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  style={underlineInputStyle}
                />
                {fieldErrors.lastNameEn && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.lastNameEn}</small>}
              </div>
            </div>

            {/* Email & Mobile Section */}
            <div 
              className="email-mobile-section"
              style={{ 
                marginTop: '30px', 
                padding: '20px', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}
            >
              <h4 style={{ marginBottom: '20px', color: '#26247b', fontWeight: 'bold' }}>
                {isRTL ? 'الايميل وارقام التواصل' : 'Email & Mobile'}
              </h4>

              {/* Email with Verify Button */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <div className="verify-row">
                  <div className="verify-input-wrapper">
                    <input
                      type="email"
                      placeholder={isRTL ? 'البريد الإلكتروني *' : 'Email *'}
                      value={email}
                      onChange={(e) => {
                        setEmail(sanitizeInvisibleChars(e.target.value));
                        setFieldErrors(prev => ({...prev, email: ''}));
                        if (EMAIL_VER) setEMAIL_VER(false);
                      }}
                      onBlur={(e) => { handleInputBlur(e); validateEmail(email); }}
                      onFocus={handleInputFocus}
                      disabled={emailOtpSending || EMAIL_VER}
                      style={{
                        ...underlineInputStyle,
                        backgroundColor: emailOtpSending ? '#f0f0f0' : EMAIL_VER ? '#e8f5e9' : '#fff'
                      }}
                    />
                    {EMAIL_VER && (
                      <span style={{
                        position: 'absolute',
                        right: isRTL ? 'auto' : '15px',
                        left: isRTL ? '15px' : 'auto',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#28a745',
                        fontSize: '20px'
                      }}>✓</span>
                    )}
                    {(emailError || fieldErrors.email) && (
                      <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>
                        {emailError || fieldErrors.email}
                      </small>
                    )}
                  </div>
                  <button
                    type="button"
                    className="verify-button"
                    onClick={sendEmailOtp}
                    disabled={emailOtpSending || !email || EMAIL_VER}
                    style={{
                      backgroundColor: EMAIL_VER ? '#28a745' : (emailOtpSending || !email) ? '#6c757d' : '#26247b'
                    }}
                  >
                    {EMAIL_VER 
                      ? (isRTL ? 'تم التحقق ✓' : 'Verified ✓')
                      : emailOtpSending 
                        ? (isRTL ? 'جاري الإرسال...' : 'Sending...') 
                        : (isRTL ? 'تحقق' : 'Verify')}
                  </button>
                </div>
              </div>

              {/* Mobile with Verify Button */}
              <div className="form-group" style={{ marginBottom: '20px' }}>
                <div className="verify-row">
                  <div className="verify-input-wrapper">
                    <input
                      type="tel"
                      ref={mobileInputRef}
                      placeholder={isRTL ? '* رقم الجوال ' : 'Mobile *'}
                      value={mobile}
                      onChange={(e) => {
                        setMobile(sanitizePhoneNumber(e.target.value));
                        setFieldErrors(prev => ({...prev, mobile: ''}));
                        if (MOBILE_VER) setMOBILE_VER(false);
                      }}
                      onBlur={(e) => { handleInputBlur(e); handleMobileValidation(); }}
                      onFocus={handleInputFocus}
                      disabled={MOBILE_VER}
                      style={{
                        ...underlineInputStyle,
                        backgroundColor: MOBILE_VER ? '#e8f5e9' : '#fff'
                      }}
                    />
                    {MOBILE_VER && (
                      <span style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#28a745',
                        fontSize: '20px',
                        zIndex: 10
                      }}>✓</span>
                    )}
                    {(mobileError || fieldErrors.mobile) && (
                      <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>
                        {mobileError || fieldErrors.mobile}
                      </small>
                    )}
                  </div>
                  {/* Hide Verify button if skipMobileOtp is enabled */}
                  {!skipMobileOtp && (
                  <button
                    type="button"
                    className="verify-button"
                    onClick={sendMobileOtp}
                    disabled={mobileOtpSending || !mobile || MOBILE_VER}
                    style={{
                      backgroundColor: MOBILE_VER ? '#28a745' : (mobileOtpSending || !mobile) ? '#6c757d' : '#26247b'
                    }}
                  >
                    {MOBILE_VER 
                      ? (isRTL ? 'تم التحقق ✓' : 'Verified ✓')
                      : mobileOtpSending 
                        ? (isRTL ? 'جاري الإرسال...' : 'Sending...') 
                        : (isRTL ? 'تحقق' : 'Verify')}
                  </button>
                  )}
                </div>
              </div>

              {/* Phone */}
              <div className="form-group" style={{ marginBottom: '0' }}>
                <input
                  type="tel"
                  ref={phoneInputRef}
                  placeholder={isRTL ? 'رقم الجوال (اختياري)' : 'Mobile (optional)'}
                  value={phone}
                  onChange={(e) => setPhone(sanitizePhoneNumber(e.target.value))}
                  onBlur={(e) => { handleInputBlur(e); handlePhoneValidation(); }}
                  onFocus={handleInputFocus}
                  style={underlineInputStyle}
                />
                {phoneError && (
                  <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>
                    {phoneError}
                  </small>
                )}
              </div>
            </div>

            {/* Rules Acceptance Section */}
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <p style={{ marginBottom: '15px', color: '#333', fontSize: '15px' }}>
                <span 
                  onClick={() => setShowRulesPopup(true)}
                  style={{ 
                    color: '#26247b', 
                    textDecoration: 'underline', 
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {isRTL 
                    ? 'لوائح وشروط القبول والتسجيل.' 
                    : 'Admission & Registration Rules.'}
                </span>
              </p>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: 'pointer',
                gap: '10px'
              }}>
                <input
                  type="checkbox"
                  checked={rulesAccepted}
                  onChange={(e) => setRulesAccepted(e.target.checked)}
                  style={{ 
                    width: '20px', 
                    height: '20px', 
                    accentColor: '#26247b',
                    cursor: 'pointer',
                    flexShrink: 0
                  }}
                />
                <span style={{ color: '#333', fontSize: '15px' }}>
                  {isRTL 
                    ? 'أقر بأنني قرأت لوائح وشروط القبول والتسجيل وأوافق عليها.' 
                    : 'I have read and accept the rules.'}
                </span>
              </label>
            </div>
          </div>
        );
      case 2:
        // Application Type options
        const applicationTypeOptions = [
          { value: 'bridging', label: isRTL ? 'بريدجنق' : 'Bridging' },
          { value: 'high_school', label: isRTL ? 'ثانوية عامة' : 'High School Applicant' },
          { value: 'academic_acceleration', label: isRTL ? 'تسريع اكاديمي' : 'Academic Acceleration' },
          { value: 'transfer', label: isRTL ? 'طالب محول' : 'Transfer Applicant' },
          { value: 'double_major', label: isRTL ? 'تخصص مزدوج' : 'Double Major Applicant' }
        ];

        // High School Curriculum options - site-specific for local high school
        const localHighSchoolValue = site.isUAE ? 'emirati_high_school' : 'saudi_high_school';
        const localHighSchoolLabel = site.isUAE 
          ? (isRTL ? 'ثانوية اماراتية' : 'Emirati High School')
          : (isRTL ? 'ثانوية سعودية' : 'Saudi High School Student');
        const curriculumOptions = [
          { value: localHighSchoolValue, label: localHighSchoolLabel },
          { value: 'international_high_school', label: isRTL ? 'ثانوية عالمية' : 'International High School Student' }
        ];

        // High School Grade options
        const gradeOptions = [
          { value: '95', label: '95%-100%' },
          { value: '90', label: '90%-94.99%' },
          { value: '80', label: '80%-89.99%' },
          { value: '60', label: '60%-79.99%' },
          { value: '59', label: 'Below 60%' },
          { value: 'A', label: 'A*, A+, A1, A-, A' },
          { value: 'B', label: 'B*, B+, B1, B-, B' },
          { value: 'C', label: 'C*, C+, C1, C-, C' },
          { value: 'D', label: 'D*, D+, D1, D-, D' },
          { value: 'E', label: 'E*, E+, E1, E-, E' }
        ];

        // Graduation Year options
        const yearOptions = [
          { value: '2024', label: '2024 - 2025' },
          { value: '2023', label: '2023 - 2024' },
          { value: '2022', label: '2022 - 2023' },
          { value: '2021', label: '2021 - 2022' },
          { value: '2020', label: '2020 - 2021' },
          { value: '2019', label: '2019 - 2020' }
        ];

        // Check application types
        const isHighSchool = intake?.application_type === 'high_school' || intake?.application_type === 'academic_acceleration';
        const isAcademicAcceleration = intake?.application_type === 'academic_acceleration';
        const isAcceleration = intake?.level === 'acceleration' && intake?.application_type === 'high_school';
        const isBridging = intake?.application_type === 'bridging';
        const isTransfer = intake?.application_type === 'transfer';
        const isDoubleMajor = intake?.application_type === 'double_major';
        const isMaster = intake?.application_type === 'master';
        const isSciFnd = intake?.level === 'sci_fnd' && intake?.application_type === 'high_school';
        const isDiploma = intake?.level === 'diploma' && intake?.application_type === 'high_school';
        // UAE high_school: simplified form - hide exam result, qudrat, tahsili, english cert, alternative major, etc.
        const isUaeHighSchool = site.isUAE && intake?.application_type === 'high_school' && !isAcademicAcceleration;

        return (
          <div>
            <h2 style={{ marginBottom: '30px', color: '#26247b' }}>{steps[1].label}</h2>

            {/* High School Curriculum and Grade Section - Hidden for master */}
            {!isMaster && (
            <div style={{ 
              marginTop: '25px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ marginBottom: '20px', color: '#26247b', fontWeight: 'bold' }}>
                {isRTL ? 'المنهج الدراسي والدرجات' : 'High School Curriculum and Grade'}
              </h4>
              
              {/* Two-column layout for curriculum and grade */}
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {/* High School Curriculum */}
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={getLabelStyle()}>
                    {isRTL ? 'المنهج الدراسي' : 'High School Curriculum'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select
                    value={highSchoolCurriculum}
                    onChange={(option) => { 
                      setHighSchoolCurriculum(option); 
                      setFieldErrors(prev => ({...prev, highSchoolCurriculum: ''})); 
                      trackFieldChange('High School Curriculum', option?.label, 2);
                      // Reset exam result and related fields when curriculum changes
                      if (option?.value !== 'saudi_high_school' && option?.value !== 'emirati_high_school') {
                        setExamResult(null);
                        setQudrat('');
                        setTahsili('');
                        // Clear QUDRAT and TAHSILI attachments for non-Saudi/Emirati curriculum
                        setAttachments(prev => ({...prev, QUDRAT: null, TAHSILI: null}));
                      }
                    }}
                    placeholder={isRTL ? 'الرجاء الاختيار *' : 'Please select *'}
                    options={curriculumOptions}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderRadius: '4px',
                        borderColor: state.isFocused ? '#26247b' : (fieldErrors.highSchoolCurriculum ? '#dc3545' : '#ced4da'),
                        boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? '#26247b' : '#ced4da'
                        }
                      })
                    }}
                  />
                  {fieldErrors.highSchoolCurriculum && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.highSchoolCurriculum}</small>}
                </div>

                {/* High School Grade */}
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={getLabelStyle()}>
                    {isRTL ? 'الدرجات' : 'High School Grades'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select
                    value={highSchoolGrade}
                    onChange={(option) => { 
                      setHighSchoolGrade(option); 
                      setFieldErrors(prev => ({...prev, highSchoolGrade: ''}));
                      trackFieldChange('High School Grades', option?.label, 2);
                      // KSA high_school/academic_acceleration MEDI restriction check - clear MEDI if restricted grade selected
                      const restrictedGrades = highSchoolCurriculum?.value === 'international_high_school'
                        ? ['80', '60', '59', 'B', 'C', 'D', 'E']
                        : ['90', '80', '60', '59', 'B', 'C', 'D', 'E'];
                      if (!site.isUAE && (intake?.application_type === 'high_school' || intake?.application_type === 'academic_acceleration') && restrictedGrades.includes(option?.value)) {
                        if (pyMajor?.value === 'MEDI') {
                          setPyMajor(null);
                          setModalType('warning');
                          setModalMessage({
                            en: 'Dear Applicant,<br/><br/>We would like to inform you that you are not currently eligible for admission to the Medicine Program based on the admission criteria. You may enroll in an alternative academic program, with the opportunity to transfer to the General Medicine program upon achieving a GPA of 4.5 in the first year, in accordance with the approved policy.<br/><br/>We wish you success in your academic journey.<br/><br/><a href="https://bmc.edu.sa/en/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">Check the admission criteria</a>',
                            ar: 'عزيزي المتقدم،<br/><br/>نود إفادتك بأنك غير مؤهل حاليًا للقبول في البرنامج الطب العام وفقاً لمعايير القبول. يمكنك الالتحاق ببرنامج أكاديمي آخر مع إمكانية التحويل إلى برنامج الطب العام عند تحقيقك معدل (4.5) في السنة الاولى وفق السياسة المعتمدة.<br/><br/>نتمنى لك التوفيق في رحلتك الأكاديمية<br/><br/><a href="https://bmc.edu.sa/ar/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">تحقق من معايير القبول</a>'
                          });
                          setShowModal(true);
                        }
                        if (alternativeMajor?.value === 'MEDI') {
                          setAlternativeMajor(null);
                          if (pyMajor?.value !== 'MEDI') {
                            setModalType('warning');
                            setModalMessage({
                              en: 'Dear Applicant,<br/><br/>We would like to inform you that you are not currently eligible for admission to the Medicine Program based on the admission criteria. You may enroll in an alternative academic program, with the opportunity to transfer to the General Medicine program upon achieving a GPA of 4.5 in the first year, in accordance with the approved policy.<br/><br/>We wish you success in your academic journey.<br/><br/><a href="https://bmc.edu.sa/en/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">Check the admission criteria</a>',
                              ar: 'عزيزي المتقدم،<br/><br/>نود إفادتك بأنك غير مؤهل حاليًا للقبول في البرنامج الطب العام وفقاً لمعايير القبول. يمكنك الالتحاق ببرنامج أكاديمي آخر مع إمكانية التحويل إلى برنامج الطب العام عند تحقيقك معدل (4.5) في السنة الاولى وفق السياسة المعتمدة.<br/><br/>نتمنى لك التوفيق في رحلتك الأكاديمية<br/><br/><a href="https://bmc.edu.sa/ar/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">تحقق من معايير القبول</a>'
                            });
                            setShowModal(true);
                          }
                        }
                      }
                    }}
                    placeholder={isRTL ? 'الرجاء الاختيار *' : 'Please select *'}
                    options={gradeOptions}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderRadius: '4px',
                        borderColor: state.isFocused ? '#26247b' : (fieldErrors.highSchoolGrade ? '#dc3545' : '#ced4da'),
                        boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? '#26247b' : '#ced4da'
                        }
                      })
                    }}
                  />
                  {fieldErrors.highSchoolGrade && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.highSchoolGrade}</small>}
                </div>
              </div>
              
              {/* UAE High School: Are you still waiting for exam results? */}
              {isUaeHighSchool && (
              <div className="form-group" style={{ marginTop: '25px' }}>
                <label style={getLabelStyle({ marginBottom: '12px' })}>
                  {isRTL ? 'هل مازلت تنتظر نتيجة اختبارات الثانوية؟' : 'Are You Still Waiting for Your Exam Results?'} <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: `2px solid ${uaeWaitingExamResult === false ? '#26247b' : '#dee2e6'}`,
                      backgroundColor: uaeWaitingExamResult === false ? '#f0f0ff' : 'white',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="uaeWaitingExamResult"
                      checked={uaeWaitingExamResult === false}
                      onChange={() => { setUaeWaitingExamResult(false); setFieldErrors(prev => ({...prev, uaeWaitingExamResult: ''})); }}
                      style={{ marginRight: isRTL ? '0' : '8px', marginLeft: isRTL ? '8px' : '0' }}
                    />
                    <span style={{ fontWeight: uaeWaitingExamResult === false ? 'bold' : 'normal', color: uaeWaitingExamResult === false ? '#26247b' : '#333' }}>
                      {isRTL ? 'لا ، لقد استلمت الشهادة' : 'No, I Have Got Them'}
                    </span>
                  </label>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      border: `2px solid ${uaeWaitingExamResult === true ? '#26247b' : '#dee2e6'}`,
                      backgroundColor: uaeWaitingExamResult === true ? '#f0f0ff' : 'white',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="uaeWaitingExamResult"
                      checked={uaeWaitingExamResult === true}
                      onChange={() => { setUaeWaitingExamResult(true); setFieldErrors(prev => ({...prev, uaeWaitingExamResult: ''})); }}
                      style={{ marginRight: isRTL ? '0' : '8px', marginLeft: isRTL ? '8px' : '0' }}
                    />
                    <span style={{ fontWeight: uaeWaitingExamResult === true ? 'bold' : 'normal', color: uaeWaitingExamResult === true ? '#26247b' : '#333' }}>
                      {isRTL ? 'نعم ، مازلت اتنظر النتائج' : 'Yes, I am Still Waiting'}
                    </span>
                  </label>
                </div>
                {fieldErrors.uaeWaitingExamResult && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.uaeWaitingExamResult}</small>}
              </div>
              )}
            </div>
            )}

            {/* Combined Radio Buttons Section - Applicant Type Related Questions - Hidden for master, UAE high_school, sci_fnd, and only shown when Saudi High School curriculum is selected */}
            {!isUaeHighSchool && !isSciFnd && !isMaster && !isBridging && !isTransfer && !isDoubleMajor && (highSchoolCurriculum?.value === 'saudi_high_school' || highSchoolCurriculum?.value === 'emirati_high_school') && (
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              {/* Exam Result Radio Buttons - Hidden for bridging, transfer, double_major, and master. Only shown when Saudi High School curriculum is selected */}
              <div className="form-group" style={{ marginBottom: '25px' }}>
                <label style={getLabelStyle({ marginBottom: '12px' })}>
                  {isRTL ? 'هل تنتظر نتيجة امتحان القدرات والتحصيلي؟' : 'Are you awaiting for Qudrat and Tahsili exam results?'} <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      border: examResult === true ? '2px solid #26247b' : '2px solid #ffffff',
                      backgroundColor: examResult === true ? '#26247b' : '#ffffff',
                      color: examResult === true ? '#ffffff' : '#333333',
                      minWidth: '100px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="examResult"
                      value="true"
                      checked={examResult === true}
                      onChange={() => { 
                        setExamResult(true); 
                        setFieldErrors(prev => ({...prev, examResult: ''})); 
                        // Clear QUDRAT and TAHSILI attachments when user selects 'Yes' (waiting for results)
                        setAttachments(prev => ({...prev, QUDRAT: null, TAHSILI: null}));
                        setQudrat('');
                        setTahsili('');
                      }}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: examResult === true ? '2px solid #ffffff' : '2px solid #26247b',
                      marginRight: isRTL ? '0' : '10px',
                      marginLeft: isRTL ? '10px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {examResult === true && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff'
                        }}></span>
                      )}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{isRTL ? 'نعم' : 'Yes'}</span>
                  </label>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      border: examResult === false ? '2px solid #26247b' : '2px solid #ffffff',
                      backgroundColor: examResult === false ? '#26247b' : '#ffffff',
                      color: examResult === false ? '#ffffff' : '#333333',
                      minWidth: '100px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="examResult"
                      value="false"
                      checked={examResult === false}
                      onChange={() => { setExamResult(false); setFieldErrors(prev => ({...prev, examResult: ''})); }}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: examResult === false ? '2px solid #ffffff' : '2px solid #26247b',
                      marginRight: isRTL ? '0' : '10px',
                      marginLeft: isRTL ? '10px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {examResult === false && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff'
                        }}></span>
                      )}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{isRTL ? 'لا' : 'No'}</span>
                  </label>
                </div>
                {fieldErrors.examResult && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.examResult}</small>}
                
                {/* Qudrat and Tahsili - Show only when examResult is No (false) */}
                {examResult === false && (
                  <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '20px' }}>
                    {/* Qudrat */}
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <input
                        type="text"
                        value={qudrat}
                        onChange={(e) => {
                          const numbersOnly = e.target.value.replace(/[^0-9.]/g, '');
                          setQudrat(numbersOnly);
                          setFieldErrors(prev => ({...prev, qudrat: ''}));
                        }}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder={isRTL ? 'القدرات *' : 'Qudrat *'}
                        style={underlineInputStyle}
                      />
                      {fieldErrors.qudrat && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.qudrat}</small>}
                    </div>

                    {/* Tahsili */}
                    <div style={{ flex: '1', minWidth: '200px' }}>
                      <input
                        type="text"
                        value={tahsili}
                        onChange={(e) => {
                          const numbersOnly = e.target.value.replace(/[^0-9.]/g, '');
                          setTahsili(numbersOnly);
                          setFieldErrors(prev => ({...prev, tahsili: ''}));
                        }}
                        onFocus={handleInputFocus}
                        onBlur={handleInputBlur}
                        placeholder={isRTL ? 'التحصيلي *' : 'Tahsili *'}
                        style={underlineInputStyle}
                      />
                      {fieldErrors.tahsili && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.tahsili}</small>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            )}

            {/* Has English Certificate Radio - Hidden for high_school, sci_fnd, and diploma applicants, shown for bridging, transfer, double_major, academic_acceleration (not master - has its own section) */}
            {((!isHighSchool || isBridging || isTransfer || isDoubleMajor || isAcademicAcceleration) && !isMaster && !isSciFnd && !isDiploma) && (
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div className="form-group" style={{ marginBottom: hasEnglishCert === true ? '20px' : '0' }}>
                <label style={getLabelStyle({ marginBottom: '12px' })}>
                  {isRTL ? 'هل لديك شهادة اللغة الانجليزية' : 'Do you have English Language Certificate'} <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      border: hasEnglishCert === true ? '2px solid #26247b' : '2px solid #ffffff',
                      backgroundColor: hasEnglishCert === true ? '#26247b' : '#ffffff',
                      color: hasEnglishCert === true ? '#ffffff' : '#333333',
                      minWidth: '100px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="hasEnglishCert"
                      value="true"
                      checked={hasEnglishCert === true}
                      onChange={() => { setHasEnglishCert(true); setFieldErrors(prev => ({...prev, hasEnglishCert: ''})); }}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: hasEnglishCert === true ? '2px solid #ffffff' : '2px solid #26247b',
                      marginRight: isRTL ? '0' : '10px',
                      marginLeft: isRTL ? '10px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {hasEnglishCert === true && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff'
                        }}></span>
                      )}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{isRTL ? 'نعم' : 'Yes'}</span>
                  </label>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      border: hasEnglishCert === false ? '2px solid #26247b' : '2px solid #ffffff',
                      backgroundColor: hasEnglishCert === false ? '#26247b' : '#ffffff',
                      color: hasEnglishCert === false ? '#ffffff' : '#333333',
                      minWidth: '100px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="hasEnglishCert"
                      value="false"
                      checked={hasEnglishCert === false}
                      onChange={() => { 
                        setHasEnglishCert(false); 
                        setEnglishCertMarks(''); 
                        setFieldErrors(prev => ({...prev, hasEnglishCert: '', englishCertMarks: ''})); 
                        // Clear ENGLISH_TEST attachment when user selects 'No'
                        setAttachments(prev => ({...prev, ENGLISH_TEST: null}));
                      }}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: hasEnglishCert === false ? '2px solid #ffffff' : '2px solid #26247b',
                      marginRight: isRTL ? '0' : '10px',
                      marginLeft: isRTL ? '10px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {hasEnglishCert === false && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff'
                        }}></span>
                      )}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{isRTL ? 'لا' : 'No'}</span>
                  </label>
                </div>
                {fieldErrors.hasEnglishCert && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.hasEnglishCert}</small>}
              </div>

              {/* English Certificate Marks - only show if hasEnglishCert is true */}
              {hasEnglishCert === true && (
                <div className="form-group">
                  <label style={getLabelStyle()}>
                    {isRTL ? 'درجة الايلتس/ التوفل/ستيبس' : 'TOEFL/IELTS/STEPS MARKS'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={englishCertMarks}
                    onChange={(e) => { setEnglishCertMarks(e.target.value); setFieldErrors(prev => ({...prev, englishCertMarks: ''})); }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={isRTL ? 'أدخل الدرجة *' : 'Enter marks *'}
                    style={{
                      ...underlineInputStyle,
                      maxWidth: '300px'
                    }}
                  />
                  {fieldErrors.englishCertMarks && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.englishCertMarks}</small>}
                </div>
              )}
            </div>
            )}

            {/* English Certificate Section for Master applicants */}
            {isMaster && (
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <div className="form-group" style={{ marginBottom: hasEnglishCert === true ? '20px' : '0' }}>
                <label style={getLabelStyle({ marginBottom: '12px' })}>
                  {isRTL ? 'هل لديك شهادة اللغة الانجليزية' : 'Do you have English Language Certificate'} <span style={{ color: 'red' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      border: hasEnglishCert === true ? '2px solid #26247b' : '2px solid #ffffff',
                      backgroundColor: hasEnglishCert === true ? '#26247b' : '#ffffff',
                      color: hasEnglishCert === true ? '#ffffff' : '#333333',
                      minWidth: '100px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="hasEnglishCertMaster"
                      value="true"
                      checked={hasEnglishCert === true}
                      onChange={() => { setHasEnglishCert(true); setFieldErrors(prev => ({...prev, hasEnglishCert: ''})); }}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: hasEnglishCert === true ? '2px solid #ffffff' : '2px solid #26247b',
                      marginRight: isRTL ? '0' : '10px',
                      marginLeft: isRTL ? '10px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {hasEnglishCert === true && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff'
                        }}></span>
                      )}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{isRTL ? 'نعم' : 'Yes'}</span>
                  </label>
                  <label 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      cursor: 'pointer',
                      padding: '12px 24px',
                      borderRadius: '6px',
                      border: hasEnglishCert === false ? '2px solid #26247b' : '2px solid #ffffff',
                      backgroundColor: hasEnglishCert === false ? '#26247b' : '#ffffff',
                      color: hasEnglishCert === false ? '#ffffff' : '#333333',
                      minWidth: '100px',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <input
                      type="radio"
                      name="hasEnglishCertMaster"
                      value="false"
                      checked={hasEnglishCert === false}
                      onChange={() => { 
                        setHasEnglishCert(false); 
                        setEnglishCertMarks(''); 
                        setFieldErrors(prev => ({...prev, hasEnglishCert: '', englishCertMarks: ''})); 
                        // Clear ENGLISH_TEST attachment when user selects 'No'
                        setAttachments(prev => ({...prev, ENGLISH_TEST: null}));
                      }}
                      style={{ display: 'none' }}
                    />
                    <span style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      border: hasEnglishCert === false ? '2px solid #ffffff' : '2px solid #26247b',
                      marginRight: isRTL ? '0' : '10px',
                      marginLeft: isRTL ? '10px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {hasEnglishCert === false && (
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: '#ffffff'
                        }}></span>
                      )}
                    </span>
                    <span style={{ fontSize: '16px', fontWeight: '500' }}>{isRTL ? 'لا' : 'No'}</span>
                  </label>
                </div>
                {fieldErrors.hasEnglishCert && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.hasEnglishCert}</small>}
              </div>

              {/* English Certificate Marks - only show if hasEnglishCert is true */}
              {hasEnglishCert === true && (
                <div className="form-group">
                  <label style={getLabelStyle()}>
                    {isRTL ? 'درجة الايلتس/ التوفل/ستيبس' : 'TOEFL/IELTS/STEPS MARKS'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={englishCertMarks}
                    onChange={(e) => { setEnglishCertMarks(e.target.value); setFieldErrors(prev => ({...prev, englishCertMarks: ''})); }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={isRTL ? 'أدخل الدرجة *' : 'Enter marks *'}
                    style={{
                      ...underlineInputStyle,
                      maxWidth: '300px'
                    }}
                  />
                  {fieldErrors.englishCertMarks && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.englishCertMarks}</small>}
                </div>
              )}
            </div>
            )}

            {/* Diploma Details Section - Hidden for high_school, academic_acceleration, sci_fnd, and diploma applicants, shown for bridging, transfer, double_major, and master */}
            {((!isHighSchool || isBridging || isTransfer || isDoubleMajor || isMaster) && !isAcademicAcceleration && !isSciFnd && !isDiploma) && (
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ marginBottom: '20px', color: '#26247b', fontWeight: 'bold' }}>
                {isRTL ? 'تفاصيل الدبلوم' : 'Diploma Details'}
              </h4>
              
              {/* Two-column layout for Higher Education Program and University/College */}
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {/* Higher Education Program */}
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={getLabelStyle()}>
                    {isRTL ? 'شهادة التخرج' : 'Higher Education Program'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={higherEducationProgram}
                    onChange={(e) => { setHigherEducationProgram(e.target.value); setFieldErrors(prev => ({...prev, higherEducationProgram: ''})); }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={isRTL ? 'أدخل البرنامج *' : 'Enter program *'}
                    style={underlineInputStyle}
                  />
                  {fieldErrors.higherEducationProgram && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.higherEducationProgram}</small>}
                </div>
              </div>

              {/* Two-column layout for University/College and GPA */}
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '20px' }}>
                {/* University/College - Hidden for bridging applicants, shown for transfer */}
                {!isBridging && (
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={getLabelStyle()}>
                    {isRTL ? 'اسم الكلية/الجامعة' : 'University/College'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={higherEducationSchool}
                    onChange={(e) => { setHigherEducationSchool(e.target.value); setFieldErrors(prev => ({...prev, higherEducationSchool: ''})); }}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder={isRTL ? 'أدخل اسم الكلية/الجامعة *' : 'Enter university/college *'}
                    style={underlineInputStyle}
                  />
                  {fieldErrors.higherEducationSchool && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.higherEducationSchool}</small>}
                </div>
                )}

                {/* GPA */}
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={getLabelStyle()}>
                    {isRTL ? 'المعدل التراكمي' : 'GPA'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={higherEducationGpa}
                    onChange={(e) => {
                      const numbersOnly = e.target.value.replace(/[^0-9.]/g, '');
                      setHigherEducationGpa(numbersOnly);
                      setFieldErrors(prev => ({...prev, higherEducationGpa: ''}));
                    }}
                    onBlur={(e) => { if (e.target.value) trackFieldChange('GPA', e.target.value, 2); handleInputBlur(e); }}
                    onFocus={handleInputFocus}
                    placeholder={isRTL ? 'أدخل المعدل *' : 'Enter GPA *'}
                    style={underlineInputStyle}
                  />
                  {fieldErrors.higherEducationGpa && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.higherEducationGpa}</small>}
                </div>
              </div>

              {/* No of Years - Hidden for transfer, double_major, and master */}
              {!isTransfer && !isDoubleMajor && !isMaster && (
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '20px' }}>
                {/* No of Years */}
                <div style={{ flex: '1', minWidth: '200px', maxWidth: '50%' }}>
                  <label style={getLabelStyle()}>
                    {isRTL ? 'عدد سنوات الدبلوم المكتملة' : 'No. of completed year in Diploma'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select
                    value={noOfYears}
                    onChange={(option) => { setNoOfYears(option); setFieldErrors(prev => ({...prev, noOfYears: ''})); }}
                    placeholder={isRTL ? 'الرجاء الاختيار *' : 'Please select *'}
                    options={[
                      { value: 'two_half_years', label: isRTL ? 'سنتان ونصف أو أقل' : '2.5 Years or Less' },
                      { value: 'three_years', label: isRTL ? 'ثلاث سنوات' : 'Three Years' }
                    ]}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderRadius: '4px',
                        borderColor: state.isFocused ? '#26247b' : (fieldErrors.noOfYears ? '#dc3545' : '#ced4da'),
                        boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? '#26247b' : '#ced4da'
                        }
                      })
                    }}
                  />
                  {fieldErrors.noOfYears && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.noOfYears}</small>}
                </div>
              </div>
              )}
            </div>
            )}

            {/* Select Major Section */}
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ marginBottom: '20px', color: '#26247b', fontWeight: 'bold' }}>
                {isRTL ? 'اختر التخصص' : 'Select Major'}
              </h4>
              
              {/* Two-column layout for Major and Preferred Program */}
              <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={getLabelStyle()}>
                    {isRTL ? 'اسم التخصص' : 'Major Name'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select
                    value={pyMajor}
                    onChange={(option) => {
                      // KSA high_school/academic_acceleration MEDI restriction check
                      const restrictedGrades = highSchoolCurriculum?.value === 'international_high_school'
                        ? ['80', '60', '59', 'B', 'C', 'D', 'E']
                        : ['90', '80', '60', '59', 'B', 'C', 'D', 'E'];
                      if (!site.isUAE && (intake?.application_type === 'high_school' || intake?.application_type === 'academic_acceleration') && option?.value === 'MEDI' && restrictedGrades.includes(highSchoolGrade?.value)) {
                        setModalType('warning');
                        setModalMessage({
                          en: 'Dear Applicant,<br/><br/>We would like to inform you that you are not currently eligible for admission to the Medicine Program based on the admission criteria. You may enroll in an alternative academic program, with the opportunity to transfer to the General Medicine program upon achieving a GPA of 4.5 in the first year, in accordance with the approved policy.<br/><br/>We wish you success in your academic journey.<br/><br/><a href="https://bmc.edu.sa/en/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">Check the admission criteria</a>',
                          ar: 'عزيزي المتقدم،<br/><br/>نود إفادتك بأنك غير مؤهل حاليًا للقبول في البرنامج الطب العام وفقاً لمعايير القبول. يمكنك الالتحاق ببرنامج أكاديمي آخر مع إمكانية التحويل إلى برنامج الطب العام عند تحقيقك معدل (4.5) في السنة الاولى وفق السياسة المعتمدة.<br/><br/>نتمنى لك التوفيق في رحلتك الأكاديمية<br/><br/><a href="https://bmc.edu.sa/ar/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">تحقق من معايير القبول</a>'
                        });
                        setShowModal(true);
                        return;
                      }
                      // Check if same major should be allowed: high_school with 2 programs (one is MEDI) and restricted grades
                      const restrictedGradesForSameMajor = highSchoolCurriculum?.value === 'international_high_school'
                        ? ['80', '60', '59', 'B', 'C', 'D', 'E']
                        : ['90', '80', '60', '59', 'B', 'C', 'D', 'E'];
                      const hasMediProgram = majorOptions.some(opt => opt.value === 'MEDI');
                      const allowSameMajor = intake?.application_type === 'high_school' && 
                                             majorOptions.length === 2 && 
                                             hasMediProgram && 
                                             restrictedGradesForSameMajor.includes(highSchoolGrade?.value);
                      // Skip same major validation if only one option available or allowSameMajor is true
                      if (!allowSameMajor && majorOptions.length > 1 && alternativeMajor && option && option.value === alternativeMajor.value) {
                        setModalType('warning');
                        setModalMessage({
                          en: 'The selected main major and alternative major must not be the same',
                          ar: 'يجب ألا يكون التخصص الرئيسي المختار والتخصص البديل متطابقين'
                        });
                        setShowModal(true);
                        return;
                      }
                      setPyMajor(option);
                      setFieldErrors(prev => ({...prev, pyMajor: ''}));
                      trackFieldChange('Main Major', option?.label, 2);
                    }}
                    placeholder={isRTL ? 'الرجاء الاختيار *' : 'Please select *'}
                    options={majorOptions}
                    isDisabled={majorOptions.length === 0}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderRadius: '4px',
                        borderColor: state.isFocused ? '#26247b' : (fieldErrors.pyMajor ? '#dc3545' : '#ced4da'),
                        boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? '#26247b' : '#ced4da'
                        }
                      })
                    }}
                  />
                  {fieldErrors.pyMajor && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.pyMajor}</small>}
                </div>

                {/* Alternative Major - Hidden for bridging, transfer, double_major, master, sci_fnd, diploma, acceleration, and UAE high_school */}
                {!isUaeHighSchool && !isBridging && !isTransfer && !isDoubleMajor && !isMaster && !isSciFnd && !isDiploma && !isAcceleration && (
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <label style={getLabelStyle()}>
                    {isRTL ? 'التخصص البديل' : 'Alternative Major'} <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select
                    value={alternativeMajor}
                    onChange={(option) => { 
                      // KSA high_school/academic_acceleration MEDI restriction check
                      const restrictedGrades = highSchoolCurriculum?.value === 'international_high_school'
                        ? ['80', '60', '59', 'B', 'C', 'D', 'E']
                        : ['90', '80', '60', '59', 'B', 'C', 'D', 'E'];
                      if (!site.isUAE && (intake?.application_type === 'high_school' || intake?.application_type === 'academic_acceleration') && option?.value === 'MEDI' && restrictedGrades.includes(highSchoolGrade?.value)) {
                        setModalType('warning');
                        setModalMessage({
                          en: 'Dear Applicant,<br/><br/>We would like to inform you that you are not currently eligible for admission to the Medicine Program based on the admission criteria. You may enroll in an alternative academic program, with the opportunity to transfer to the General Medicine program upon achieving a GPA of 4.5 in the first year, in accordance with the approved policy.<br/><br/>We wish you success in your academic journey.<br/><br/><a href="https://bmc.edu.sa/en/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">Check the admission criteria</a>',
                          ar: 'عزيزي المتقدم،<br/><br/>نود إفادتك بأنك غير مؤهل حاليًا للقبول في البرنامج الطب العام وفقاً لمعايير القبول. يمكنك الالتحاق ببرنامج أكاديمي آخر مع إمكانية التحويل إلى برنامج الطب العام عند تحقيقك معدل (4.5) في السنة الاولى وفق السياسة المعتمدة.<br/><br/>نتمنى لك التوفيق في رحلتك الأكاديمية<br/><br/><a href="https://bmc.edu.sa/ar/Admissions-Aid/Admissions" target="_blank" rel="noopener noreferrer" style="color: #26247b; font-weight: bold;">تحقق من معايير القبول</a>'
                        });
                        setShowModal(true);
                        return;
                      }
                      // Check if same major should be allowed: high_school with 2 programs (one is MEDI) and restricted grades
                      const restrictedGradesForSameMajor = highSchoolCurriculum?.value === 'international_high_school'
                        ? ['80', '60', '59', 'B', 'C', 'D', 'E']
                        : ['90', '80', '60', '59', 'B', 'C', 'D', 'E'];
                      const hasMediProgram = majorOptions.some(opt => opt.value === 'MEDI');
                      const allowSameMajor = intake?.application_type === 'high_school' && 
                                             majorOptions.length === 2 && 
                                             hasMediProgram && 
                                             restrictedGradesForSameMajor.includes(highSchoolGrade?.value);
                      // Skip same major validation if only one option available or allowSameMajor is true
                      if (!allowSameMajor && majorOptions.length > 1 && option && pyMajor && option.value === pyMajor.value) {
                        setModalType('warning');
                        setModalMessage({
                          en: 'The selected main major and alternative major must not be the same',
                          ar: 'يجب ألا يكون التخصص الرئيسي المختار والتخصص البديل متطابقين'
                        });
                        setShowModal(true);
                        return;
                      }
                      setAlternativeMajor(option); 
                      setFieldErrors(prev => ({...prev, alternativeMajor: ''})); 
                    }}
                    placeholder={isRTL ? 'الرجاء الاختيار' : 'Please select'}
                    options={majorOptions}
                    isDisabled={majorOptions.length === 0}
                    isClearable
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        minHeight: '44px',
                        borderRadius: '4px',
                        borderColor: state.isFocused ? '#26247b' : (fieldErrors.alternativeMajor ? '#dc3545' : '#ced4da'),
                        boxShadow: state.isFocused ? '0 0 0 1px #26247b' : 'none',
                        '&:hover': {
                          borderColor: state.isFocused ? '#26247b' : '#ced4da'
                        }
                      })
                    }}
                  />
                  {fieldErrors.alternativeMajor && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors.alternativeMajor}</small>}
                </div>
                )}
              </div>
            </div>

            {/* Attachment Section */}
            <div style={{ 
              marginTop: '30px', 
              padding: '20px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ marginBottom: '10px', color: '#26247b', fontWeight: 'bold' }}>
                {isRTL ? 'المرفقات' : 'Attachment'}
              </h4>
              <p style={{ marginBottom: '20px', color: '#6c757d', fontSize: '14px' }}>
                {isRTL 
                  ? 'يسمح فقط بملفات jpeg, pdf, png. الحد الأقصى للحجم 3 MB' 
                  : 'Only jpeg, pdf, png. Maximum size 3 MB'}
              </p>
              
              {/* Attachment fields - 2 per row */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                {[
                  { key: 'HIGH_SCHOOL_TRAN', labelEn: 'High School Transcript', labelAr: 'شهادة الثانوية العامة', show: (isUaeHighSchool ? uaeWaitingExamResult === true : !isMaster), requiredCondition: (isUaeHighSchool ? uaeWaitingExamResult === true : !isMaster) },
                  { key: 'QUDRAT', labelEn: 'Qudrat Certificate', labelAr: 'شهادة القدرات', show: !isUaeHighSchool && !isSciFnd && ((!isHighSchool || examResult === false) && !isBridging && !isTransfer && !isDoubleMajor && !isMaster), requiredCondition: !isUaeHighSchool && !isSciFnd && (examResult === false && !isBridging && !isTransfer && !isDoubleMajor && !isMaster) },
                  { key: 'TAHSILI', labelEn: 'Tahsili Certificate', labelAr: 'شهادة التحصيلي', show: !isUaeHighSchool && !isSciFnd && ((!isHighSchool || examResult === false) && !isBridging && !isTransfer && !isDoubleMajor && !isMaster), requiredCondition: !isUaeHighSchool && !isSciFnd && (examResult === false && !isBridging && !isTransfer && !isDoubleMajor && !isMaster) },
                  { key: 'LATEST_TRAN', labelEn: 'Latest Transcript', labelAr: 'السجل الاكاديمي', show: !isUaeHighSchool && (!isHighSchool || isBridging || isTransfer || isDoubleMajor || isMaster) && !isSciFnd, requiredCondition: !isUaeHighSchool && (!isHighSchool || isBridging || isTransfer || isDoubleMajor || isMaster) && !isSciFnd },
                  { key: 'ENGLISH_TEST', labelEn: 'English Test', labelAr: 'شهادة الانجليزي', show: !isUaeHighSchool && ((!isHighSchool || isBridging || isTransfer || isDoubleMajor || isMaster || (isAcademicAcceleration && hasEnglishCert)) && ((isBridging || isTransfer || isDoubleMajor || isMaster || isAcademicAcceleration) ? hasEnglishCert : true)) && !isSciFnd, requiredCondition: !isUaeHighSchool && ((isBridging || isTransfer || isDoubleMajor || isMaster || isAcademicAcceleration) ? hasEnglishCert : !isHighSchool) && !isSciFnd },
                  { key: 'TASNEEF', labelEn: 'Tasneef', labelAr: 'شهادة التصنيف', show: !isUaeHighSchool && !isHighSchool && !(isBridging && site.isUAE) && !isTransfer && !isDoubleMajor && !isMaster && !isSciFnd, requiredCondition: !isUaeHighSchool && !isHighSchool && !(isBridging && site.isUAE) && !isTransfer && !isDoubleMajor && !isMaster && !isSciFnd },
                  { key: 'GRAD_CERT', labelEn: 'Graduation Certificate', labelAr: 'شهادة التخرج', show: (isUaeHighSchool ? uaeWaitingExamResult === false : ((!isHighSchool || isBridging || isTransfer || isDoubleMajor || isMaster) && !isSciFnd)), requiredCondition: (isUaeHighSchool ? uaeWaitingExamResult === false : ((!isHighSchool || isBridging || isTransfer || isDoubleMajor || isMaster) && !isSciFnd)) }
                ].filter(attachment => attachment.show).map((attachment) => (
                  <div key={attachment.key} style={{ flex: '1 1 calc(50% - 10px)', minWidth: '280px', marginBottom: '10px' }}>
                    <label style={getLabelStyle({ fontSize: '14px' })}>
                      {isRTL ? attachment.labelAr : attachment.labelEn} {attachment.requiredCondition && <span style={{ color: 'red' }}>*</span>}
                    </label>
                    <input
                      ref={(ref) => {
                        if (ref) ref.setAttribute('style', 'display: none');
                      }}
                      id={`file-input-${attachment.key}`}
                      type="file"
                      accept=".jpeg,.jpg,.pdf,.png"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          // Validate file type
                          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
                          if (!validTypes.includes(file.type)) {
                            setModalType('warning');
                            setModalMessage({
                              en: 'Invalid file type. Only jpeg, pdf, png files are allowed.',
                              ar: 'نوع الملف غير صالح. يسمح فقط بملفات jpeg, pdf, png.'
                            });
                            setShowModal(true);
                            e.target.value = '';
                            return;
                          }
                          // Validate file size (3MB = 3 * 1024 * 1024 bytes)
                          if (file.size > 3 * 1024 * 1024) {
                            setModalType('warning');
                            setModalMessage({
                              en: 'File size exceeds 3 MB limit.',
                              ar: 'حجم الملف يتجاوز الحد الأقصى 3 MB.'
                            });
                            setShowModal(true);
                            e.target.value = '';
                            return;
                          }
                          setAttachments(prev => ({ ...prev, [attachment.key]: file }));
                          setFieldErrors(prev => ({ ...prev, [attachment.key]: '' }));
                        }
                      }}
                    />
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      border: `1px solid ${fieldErrors[attachment.key] ? '#dc3545' : '#ced4da'}`,
                      borderRadius: '4px',
                      backgroundColor: '#f8f9fa',
                      padding: '8px 12px',
                      boxSizing: 'border-box'
                    }}>
                      <Button
                        variant="contained"
                        size="small"
                        component="label"
                        htmlFor={`file-input-${attachment.key}`}
                        style={{
                          marginRight: isRTL ? '0' : '12px',
                          marginLeft: isRTL ? '12px' : '0',
                          minWidth: '120px',
                          textTransform: 'none',
                          fontSize: '14px',
                          backgroundColor: '#26247B',
                          border: '1px solid #26247B',
                          color: '#fff'
                        }}
                      >
                        {isRTL ? 'اختر ملف' : 'Choose File'}
                      </Button>
                      <span style={{
                        fontSize: '14px',
                        color: attachments[attachment.key] ? '#28a745' : '#6c757d',
                        fontWeight: attachments[attachment.key] ? 'bold' : 'normal',
                        flex: 1,
                        textAlign: isRTL ? 'right' : 'left',
                        direction: isRTL ? 'rtl' : 'ltr',
                        wordBreak: 'break-word'
                      }}>
                        {attachments[attachment.key] ? attachments[attachment.key].name : (isRTL ? 'لم يتم اختيار ملف' : 'No file chosen')}
                      </span>
                    </div>
                    {attachments[attachment.key] && (
                      <small style={{ color: '#28a745', display: 'block', marginTop: '5px' }}>
                        ✓ {isRTL ? 'تم تحميل الملف بنجاح' : 'File uploaded successfully'}
                      </small>
                    )}
                    {fieldErrors[attachment.key] && <small style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>{fieldErrors[attachment.key]}</small>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 3:
        const thankYouMessage = fields?.thankYouMessage?.value || (isRTL 
          ? '<p>شكرا لك على تقديم طلبك. سنتواصل معك قريبا.</p>' 
          : '<p>Thank you for submitting your application. We will contact you soon.</p>');
        const odooLoginUrl = fields?.odooLoginUrl?.value?.href || '#';
        const loginButtonText = fields?.loginButtonText?.value || (isRTL ? 'تسجيل الدخول' : 'Login to Portal');
        
        return (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div 
              style={{ fontSize: '18px', marginBottom: '40px', lineHeight: '1.6' }}
              dangerouslySetInnerHTML={{ __html: thankYouMessage }}
            />
            {odooLoginUrl && odooLoginUrl !== '#' && (
              <a 
                href={odooLoginUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="clean-button"
                style={{ 
                  display: 'inline-block',
                  padding: '16px 36px',
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#110F5E',
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderRadius: '100px',
                  boxShadow: '0 0 0 2px #110F5E',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#110F5E';
                  e.currentTarget.style.color = '#fff';
                  e.currentTarget.style.borderRadius = '12px';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#110F5E';
                  e.currentTarget.style.borderRadius = '100px';
                }}
              >
                {loginButtonText}
              </a>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="application-wizard" style={wizardStyle} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Validation Modal */}
      {showModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowModal(false)}
        >
          <div 
            style={{
              backgroundColor: '#fff',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '500px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              textAlign: 'center',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: '48px', marginBottom: '20px', textAlign: 'center' }}>
              {modalType === 'success' ? '✅' : modalType === 'info' ? 'ℹ️' : '⚠️'}
            </div>
            <p style={{ fontSize: '16px', lineHeight: '1.6', marginBottom: '25px', color: '#333' }} dangerouslySetInnerHTML={{ __html: isRTL ? modalMessage.ar : modalMessage.en }}>
            </p>
            <button
              onClick={() => setShowModal(false)}
              style={{
                padding: '10px 30px',
                backgroundColor: '#26247b',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              {isRTL ? 'موافق' : 'OK'}
            </button>
          </div>
        </div>
      )}

      {/* Rules Popup Modal */}
      {showRulesPopup && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setShowRulesPopup(false)}
        >
          <div 
            style={{
              backgroundColor: '#fff',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '700px',
              maxHeight: '80vh',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              direction: isRTL ? 'rtl' : 'ltr',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ 
              color: '#26247b', 
              marginBottom: '20px', 
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: 'bold'
            }}>
              {isRTL ? 'لوائح وشروط القبول والتسجيل' : 'Admission & Registration Rules'}
            </h3>
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              marginBottom: '20px',
              paddingRight: isRTL ? '0' : '10px',
              paddingLeft: isRTL ? '10px' : '0'
            }}>
              {getAccordionItems().length > 0 ? (
                getAccordionItems().map((item, index) => {
                  const title = item?.fields?.title?.value || item?.fields?.title || '';
                  const description = item?.fields?.description?.value || item?.fields?.description || '';
                  
                  return (
                    <div key={index} style={{ marginBottom: '20px' }}>
                      <h4 style={{ 
                        color: '#26247b', 
                        marginBottom: '10px',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}>
                        {title}
                      </h4>
                      <div 
                        style={{ 
                          color: '#444', 
                          lineHeight: '1.8',
                          fontSize: '14px',
                          textAlign: isRTL ? 'right' : 'left'
                        }}
                        dangerouslySetInnerHTML={{ __html: description }}
                      />
                    </div>
                  );
                })
              ) : (
                <p style={{ color: '#666', textAlign: 'center' }}>
                  {isRTL ? 'لا توجد قواعد متاحة.' : 'No rules available.'}
                </p>
              )}
            </div>
            <button
              onClick={() => setShowRulesPopup(false)}
              style={{
                padding: '12px 40px',
                backgroundColor: '#26247b',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                fontSize: '16px',
                cursor: 'pointer',
                fontWeight: 'bold',
                alignSelf: 'center'
              }}
            >
              {isRTL ? 'موافق' : 'OK'}
            </button>
          </div>
        </div>
      )}

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div 
            style={{
              backgroundColor: '#fff',
              padding: '30px',
              borderRadius: '8px',
              maxWidth: '450px',
              width: '90%',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              textAlign: isRTL ? 'right' : 'center',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeOtpModal}
              style={{
                position: 'absolute',
                top: '10px',
                right: isRTL ? 'auto' : '10px',
                left: isRTL ? '10px' : 'auto',
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>

            {/* Title */}
            <div style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '10px', color: '#333', textAlign: 'center' }}>
              {otpType === 'email' 
                ? (isRTL ? 'تحقق من البريد الإلكتروني' : 'Verify Email')
                : (isRTL ? 'تحقق من رقم الجوال' : 'Verify Mobile')
              }
            </div>

            {/* Description */}
            <p style={{ color: '#666', marginBottom: '10px', fontSize: '14px', textAlign: 'center' }}>
              {otpType === 'email'
                ? (isRTL 
                    ? <>تم إرسال رمز التحقق عبر البريد الإلكتروني <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 512 512" style={{ verticalAlign: 'middle', marginRight: '2px', marginLeft: '2px' }}><path fill="#0078D4" d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z"/></svg> إلى {email}</>
                    : <>A verification code has been sent by Email <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 512 512" style={{ verticalAlign: 'middle', marginRight: '2px', marginLeft: '2px' }}><path fill="#0078D4" d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z"/></svg> to {email}</>)
                : (isRTL 
                    ? <>تم إرسال رمز التحقق عن طريق الواتس اب <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 448 512" style={{ verticalAlign: 'middle', marginRight: '2px', marginLeft: '2px' }}><path fill="#25D366" d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg> إلى {mobile}</>
                    : <>A verification code has been sent by Whatsapp <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 448 512" style={{ verticalAlign: 'middle', marginRight: '2px', marginLeft: '2px' }}><path fill="#25D366" d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.1-157zm-157 341.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.6-186.6 184.6zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg> to {mobile}</>)
              }
            </p>

            {/* Email spam notice - only show for email OTP */}
            {otpType === 'email' && (
              <p style={{ 
                color: '#856404', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeeba',
                borderRadius: '4px',
                padding: '10px 12px', 
                marginBottom: '20px', 
                fontSize: '12px', 
                textAlign: 'center' 
              }}>
                {isRTL 
                  ? 'يرجى التأكد من أن صندوق البريد الإلكتروني غير ممتلئ، والتحقق من جميع المجلدات، خاصة البريد غير الهام أو الرسائل غير المرغوب فيها، حيث قد يتم تحويل رسالة رمز التحقق إليها.'
                  : 'Please make sure your email inbox is not full and check all folders, especially Spam or Junk, as the OTP email may be filtered there.'}
              </p>
            )}

            {/* OTP Input Grid - 6 boxes */}
            <div 
              style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '10px', 
                marginBottom: '20px',
                direction: 'ltr' // Always LTR for number input
              }}
              onPaste={handleOtpPaste}
            >
              {otpDigits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => otpInputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpDigitChange(index, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  style={{
                    width: '50px',
                    height: '55px',
                    textAlign: 'center',
                    fontSize: '24px',
                    fontWeight: 'bold',
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    outline: 'none',
                    transition: 'border-color 0.2s'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#007bff'}
                  onBlur={(e) => e.target.style.borderColor = '#ddd'}
                />
              ))}
            </div>

            {/* Timer */}
            {otpTimer > 0 && (
              <div style={{ color: '#666', marginBottom: '15px', fontSize: '14px', textAlign: 'center' }}>
                {isRTL ? 'الوقت المتبقي: ' : 'Time remaining: '}
                <span style={{ fontWeight: 'bold', color: otpTimer < 30 ? '#dc3545' : '#17a2b8' }}>
                  {formatTimer(otpTimer)}
                </span>
              </div>
            )}

            {/* Error Message */}
            {otpError && (
              <div style={{ color: '#dc3545', marginBottom: '15px', fontSize: '14px' }}>
                {otpError}
              </div>
            )}

            {/* Verify Button */}
            <button
              onClick={otpType === 'email' ? verifyEmailOtp : verifyMobileOtp}
              disabled={otpVerifying || otpDigits.join('').length !== 6}
              style={{
                width: '100%',
                padding: '12px 20px',
                fontSize: '16px',
                fontWeight: 'bold',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: (otpVerifying || otpDigits.join('').length !== 6) ? '#6c757d' : '#26247b',
                color: '#fff',
                cursor: (otpVerifying || otpDigits.join('').length !== 6) ? 'not-allowed' : 'pointer',
                marginBottom: '15px',
                textAlign: 'center'
              }}
            >
              {otpVerifying 
                ? (isRTL ? 'جاري التحقق...' : 'Verifying...')
                : (isRTL ? 'تحقق' : 'Verify')
              }
            </button>

            {/* Resend Link */}
            <div style={{ fontSize: '14px', textAlign: 'center' }}>
              <span style={{ color: '#666' }}>
                {isRTL ? 'لم تستلم الرمز؟ ' : "Didn't receive the code? "}
              </span>
              <button
                onClick={resendOtp}
                disabled={otpTimer > 0}
                style={{
                  background: 'none',
                  border: 'none',
                  color: otpTimer > 0 ? '#999' : '#007bff',
                  cursor: otpTimer > 0 ? 'not-allowed' : 'pointer',
                  textDecoration: otpTimer > 0 ? 'none' : 'underline',
                  fontSize: '14px',
                  padding: 0
                }}
              >
                {isRTL ? 'إعادة الإرسال' : 'Resend'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Page Loading Overlay */}
      {isSubmitting && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            width: '50px',
            height: '50px',
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '20px', fontSize: '18px', color: '#333' }}>
            {isRTL ? 'جاري إرسال البيانات...' : 'Submitting your data...'}
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Tabs Navigation - Material-UI v3 Stepper */}
      <div style={stepperStyle}>
        <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%' }}>
          <Stepper 
            activeStep={currentStep - 1} 
            connector={<ColorlibConnector />}
            style={{ direction: isRTL ? 'rtl' : 'ltr', width: '100%' }}
          >
          {steps.map((step) => (
            <Step 
              key={step.id}
              completed={currentStep > step.id || (currentStep === 3 && step.id === 3)}
              style={{ direction: isRTL ? 'rtl' : 'ltr' }}
            >
              <StepLabel
                StepIconComponent={ColorlibStepIcon}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  paddingTop: '10px',
                }}
              >
                <div 
                  style={{ 
                    fontSize: '14px',
                    marginTop: '12px',
                    maxWidth: '80px',
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    color: '#26247b',
                  }}
                  dangerouslySetInnerHTML={{ __html: step.label }}
                />
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        </div>
      </div>

      {/* Step Content */}
      <div style={contentStyle}>
        {renderStepContent()}
      </div>

      {/* Navigation Buttons */}
      {currentStep < steps.length && (
        <div style={buttonContainerStyle}>
          {/* DEV BUTTON - Hidden on live production sites */}
          {!isLiveProduction() && currentStep === 2 && (
            <button 
              style={{
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                backgroundColor: '#26247b',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: isRTL ? '0' : '10px',
                marginLeft: isRTL ? '10px' : '0'
              }}
              onClick={() => {
                setCurrentStep(3);
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
              }}
            >
              🔧 DEV: Skip to Tab 3
            </button>
          )}
          {/* DEV BUTTON - Hidden on live production sites */}
          {!isLiveProduction() && currentStep === 1 && (
            <button 
              style={{
                padding: '12px 30px',
                fontSize: '16px',
                fontWeight: 'bold',
                backgroundColor: '#26247b',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                marginRight: isRTL ? '0' : '10px',
                marginLeft: isRTL ? '10px' : '0'
              }}
              onClick={() => {
                setCurrentStep(2);
                setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
              }}
            >
              🔧 DEV: Skip to Tab 2
            </button>
          )}
          <button 
            onClick={handleNext}
            disabled={isSubmitting}
            className="next-button clean-button"
          >
            {isSubmitting ? (
              <>
                <span style={{
                  display: 'inline-block',
                  width: '16px',
                  height: '16px',
                  border: '2px solid currentColor',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                {isRTL ? 'جاري الإرسال...' : 'Submitting...'}
              </>
            ) : (
              <>
                {nextButtonLabel}
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"></path>
                </svg>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default withTranslation()(ApplicationWizard);






